/////////////////////////////////////////////////////////////////////////////////////////////////////////
// 화일명 : AgGrid.tsx
// 설명   : 데이터 그리드 공통 컴포넌트 (ag-Grid 기반)
// 기능   : - 다국어 지원
//          - C/U/D 상태별 행 스타일 적용
//          - 공통 이벤트 전달
// 작성자 : 박동근
// 최종수정일 : 2025-10-11
/////////////////////////////////////////////////////////////////////////////////////////////////////////

import React, { forwardRef, useRef, useMemo, useCallback, useEffect, useState, useReducer } from "react";
import axios from 'axios';
import { Menu, MenuItem, Divider, Box } from '@mui/material';
import { AgGridReact } from "ag-grid-react";
import { ModuleRegistry, ColumnApiModule, CellApiModule, RenderApiModule, EventApiModule, ScrollApiModule, CheckboxEditorModule } from "ag-grid-community";
import type { ColDef, ColGroupDef, GridOptions } from "ag-grid-community";
// Ensure enterprise modules register against the same core as the grid
// Sidebar/ToolPanel removed per request
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";

////////////////////////////////////////////////////////////////////////////////////////////////////////////
/// 사용자정의 컴포넌트 import
////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { API_BASE_URL } from '../config';
import { useCommonTranslation } from "../translations";
import { getUserInfo } from '../utils/userUtils';
import { resolveComponent } from '../utils/comptMapUtil';
import { pgmIdOf } from '../utils/pgmIdUtil'; // 호출프로그램ID -> 화면ID(레퍼런스팝업 매핑 키 대체값)
import { loadGridRMenu, findGridRMenu, parseParamPairs, type GridRMenuRow } from '../utils/gridRMenuUtil';
import { useRefPopupModal, useMsgBoxModal } from './ModalContext';
import AgGridColsChooser from './AgGridColsChooser';

// getColumnState()/applyColumnState()/resetColumnState()는 ColumnApiModule에 속함 - 화면별 등록 목록에
// 이 모듈이 빠져있으면 조용히 빈 값을 반환(콘솔 경고만 뜨고 예외는 안남)하므로 여기서 한 번 등록해 보장함.
// 2026-09-24: 범위선택 복사에서 쓰는 api.getCellValue()(CellApiModule)/api.refreshCells()(RenderApiModule)도 같은 이유로 여기서 등록함
// - 빠져있으면 getCellValue가 undefined를 반환해 복사 결과가 빈 셀로 나오고, refreshCells가 동작하지 않아 범위 하이라이트가 안 보임.
// 2026-09-27: GridHeaderPinToggle에서 쓰는 api.addEventListener()(EventApiModule),
// api.ensureIndexVisible()(ScrollApiModule)도 같은 이유로 등록함 - 빠져있으면 "AG Grid: error #200"
// 콘솔에러가 나고(각각 columnPinned 리스너 미등록/포커스행 자동스크롤 안 됨) 해당 기능이 조용히 안 먹힘.
// 2026-09-28: checkboxColDef()(commonUtils.tsx)가 쓰는 agCheckboxCellEditor도 CheckboxEditorModule이
// 없으면 "AG Grid: error #200"이 남 - 화면별로 각자 등록하던 방식은 화면 수만큼 빠뜨리기 쉬워(실제로
// 13개 화면에서 누락) 여기서 한 번 등록해 보장함(화면별 등록은 중복이라도 무해하니 그대로 둬도 됨).
ModuleRegistry.registerModules([ColumnApiModule, CellApiModule, RenderApiModule, EventApiModule, ScrollApiModule, CheckboxEditorModule]);

// ag-Grid 표준 속성이 아닌 커스텀 확장 - 화면에서 컬럼정의에 required:true를 주면
// 아래 mergedDefaultColDef의 cellClassRules가 이 값을 읽어 표시함(입력필수 컬럼 표시용).
// - required만 있으면: 행추가(cud==='C') 행에서만 연노란색.
// - required + pk(Primary key, 예: 화면에서 editable을 cud==='C'일 때만 허용하는 컬럼)면:
//   조회된 기존 행(cud!=='C')에서 연회색으로 표시(신규 행에서는 required 규칙대로 연노란색).
// 2026-09-11: pk 속성 추가(required와 분리) - 실제 editable 여부는 여전히 각 화면의 colDef.editable에서 그대로 제어함.
// 모듈 보강(declaration merging)으로 ColDef에 추가해서, 각 화면의 기존 ColDef[] 타입 그대로 사용 가능.
declare module "ag-grid-community" {
  interface ColDef<TData = any, TValue = any> {
    required?: boolean;
    pk?: boolean;
    // 2026-09-16: applyAlign()이 런타임에서 실제로 읽어 쓰는 커스텀 속성 - 타입 선언이 빠져 있어서
    // 화면에서 align/valign을 쓰면 (as ColDef[] 캐스트 없이는) 컴파일 에러가 났음.
    // 리터럴 유니온('left'|'center'|'right' 등)으로 선언하면 withDDHeader<T>() 같은 제네릭 헬퍼를 통과할 때
    // 배열 내 다른 원소와 리터럴 추론이 충돌하는 경우가 있어(Z2002M1.tsx에서 발견) 느슨한 string으로 선언함 -
    // applyAlign()이 알 수 없는 값은 JUSTIFY_CONTENT_MAP/ALIGN_ITEMS_MAP에서 기본값으로 안전하게 처리함.
    align?: string;
    valign?: string;
    // 2026-09-22: pk:true 컬럼은 바로 위 주석대로 신규행(cud==='C')에서 자동으로 editable해지는데,
    // 값을 직접 타이핑하지 않고 공통팝업(onCellDoubleClicked -> showCommonPopup -> setCellValue)으로만
    // 채워야 하는 컬럼(예: Z4007M1 예외대상자 탭의 user_id)은 이 자동 editable이 오히려 "팝업 없이 아무
    // 값이나 직접 입력"할 수 있는 구멍이 됨. popupOnly:true를 pk와 함께 주면 신규행에서도 editable을 강제로
    // false로 고정(더블클릭 이벤트 자체는 editable과 무관하게 그대로 발생하므로 팝업은 계속 열림).
    // 2026-10-05: pk가 아닌 컬럼에도 적용(예: S5101M1 pl_id) - 항상 직접입력 불가, 더블클릭(팝업)은 기존행에서도 화면으로 전달.
    popupOnly?: boolean;
    // 2026-10-17: 직접 입력도 되고 팝업으로도 고르는 컬럼(예: T3106M1 scrt_no) - popupOnly와 같은 오른쪽 팝업 아이콘을 붙이되
    // editable은 건드리지 않음(pk면 신규행만 입력). 셀 더블클릭은 편집이 시작되므로 팝업은 아이콘으로 엶(아이콘 = 더블클릭 이벤트, 같은 pk/cud 가드).
    popupInput?: boolean;
    // 2026-09-24: 컬럼 그룹핑의 소계/합계 행에서 이 컬럼을 합산하지 않음(단가/비율처럼 합계가 의미없는 숫자 컬럼용).
    // 기본은 type:'numericColumn'인 컬럼을 전부 합산함.
    noSum?: boolean;
  }
}

// (Removed) Enterprise module registration for sidebar/tool panels


/////////////////////////////////////////////////////////////////////////////////////////////////////////
// interface : AgGridProps
/////////////////////////////////////////////////////////////////////////////////////////////////////////
export interface AgGridProps<T> {
  agGridType?: { type: "Q" | "I" };
  agGrid?: React.RefObject<AgGridReact<T> | null>;
  agGridRows: T[];
  agGridCols: (ColDef<T> | ColGroupDef<T>)[];
  agGridColsDefault: ColDef<T>;
  loading?: boolean;
  pagination?: boolean;
  pageSize?: number;
  pageSizeSelector?: number[];
  rowSelection?: { type: "single" | "multiple" };
  isRowSelectable?: (node: any) => boolean;
  suppressRowClickSelection?: boolean;
  height?: string | number;
  rowHeight?: number; // ag-Grid 행 높이(px) - 미지정 시 기본 30
  eventHandlers?: Record<string, (event: any) => void>;
  /**
   * 2026-09-18: 입력가능(I) 그리드에서 셀 값을 바꾸면 cud를 'U'로 표기하는 처리(setUpdateRow)를 공통화하기
   * 위한 옵트인 prop - 화면의 useState 세터(setGrid1/setGrid2 등)를 그대로 넘기면 셀 편집 시 자동으로
   * setUpdateRow(e.api, setAgGridRows, e)를 호출해줌. 화면은 eventHandlers.onCellValueChanged에
   * pk컬럼 중복체크 같은 "화면별 검증"만 넣으면 되고, 검증 실패로 값을 되돌려서 U표기를 하면 안 되는
   * 경우엔 그 핸들러가 명시적으로 false를 반환하면 자동표기를 건너뜀. 안 넘기면(레거시 화면) 기존처럼
   * 화면이 자기 onCellValueChanged 안에서 직접 setUpdateRow를 호출해야 함(하위호환, 동작 변화 없음).
   */
  setAgGridRows?: (fn: (prev: T[]) => T[]) => void;
  /**
   * callPgmId+gridId를 둘 다 지정하면 화면 오픈 시(onGridReady) ZA_USER_GRID에 저장된 컬럼 너비/순서/고정(pinned)을
   * 자동 복원함(기기 상관없이 같은 계정이면 동일). 저장은 자동으로 하지 않음 - 사용자가 명시적으로
   * saveGridLayout()/resetGridLayout()을 호출(예: 툴바 버튼)해야 DB에 반영됨.
   */
  callPgmId?: string;
  gridId?: string;
  /** 화면별 행 강조 규칙(행 클래스 조건) - 기본 규칙(row-created 등)에 추가로 병합됨 */
  rowClassRules?: Record<string, (params: any) => boolean>;
  /** 2026-10-10: 행별 인라인 스타일(배경색 등, 예: ZM901M1의 조건부 서식 ROW 규칙) - ag-Grid에 그대로 전달, 지금은 다른 곳에서 안 써서 병합 걱정 없음 */
  getRowStyle?: (params: any) => any;
  /**
   * 2026-09-18: 우클릭메뉴 [레퍼런스팝업 열기] 매핑(ZA_GRID_RMENU) 조회를 MENU_ID 기준으로 전환 -
   * 화면의 실제 ZA_MENU.MENU_ID(mainFrame이 tab.id로 각 화면에 내려줌)를 넘기면 findGridRMenu가
   * callPgmId 대신 이 값으로 매칭함(DD의 menuId 전환과 동일 이유 - 같은 화면을 여러 메뉴가 공유해도
   * 메뉴별로 다른 팝업 매핑을 가질 수 있음). 안 넘기면 기존처럼 programId로 매칭(하위호환).
   */
  menuId?: string;
}

/////////////////////////////////////////////////////////////////////////////////////////////////////////
// function : saveGridLayout
// 설명     : 현재 그리드의 컬럼 순서/너비와 페이지 크기/페이징 보임여부/페이지크기 선택목록을 ZA_USER_GRID에 저장
//           (사용자가 명시적으로 호출 - 자동저장 아님)
//  - 이 셋은 컬럼 단위 값이 아니라서 OBJ_ID='pageSize'/'pagination'/'pageSizeSelector' 행에 각각
//    PAGESIZE/PAGINATION/PAGESIZESELECTOR 컬럼만 채워서 같이 보냄(2026-10-07).
//    PAGINATION은 'true'/'false' 문자열, PAGESIZESELECTOR는 배열을 JSON 문자열로.
//    복원(restoreGridLayout)은 아직 이 값들을 쓰지 않음 - 저장만 우선 적용.
//  - menuId(화면을 연 메뉴ID)도 같이 저장함(2026-10-07) - PGM_ID(CALL_PGM_ID)는 메뉴 여러 개가 화면을
//    공유하면 구분이 안 될 수 있어서 보조로 둠.
// 사용예시 : saveGridLayout(callPgmId, 'agGrid1', agGrid1.current?.api, menuId);
/////////////////////////////////////////////////////////////////////////////////////////////////////////
// 2026-09-21: gridApi -> 마지막으로 복원/저장한 레이아웃(순서/너비/고정) 상태.
//  - columnDefs가 나중에 바뀌면(콤보/라벨 비동기 로딩 등) ag-Grid가 컬럼 순서/너비를 정의값으로 되돌리는데(숨김은 유지됨),
//    복원 응답이 그보다 먼저 도착하면 복원한 순서/너비가 덮이므로 columnDefs 변경 직후 이 값으로 다시 적용함.
//  - 저장하면 저장한 값으로, 초기화하면 삭제로 갱신해서 항상 "사용자가 확정한 레이아웃"만 재적용되게 함.
const savedLayoutByApi = new WeakMap<any, any[]>();

export async function saveGridLayout(callPgmId: string, gridId: string, gridApi: any, menuId?: string, asDefault?: boolean): Promise<boolean> {
  const userId = getUserInfo()?.userId || '';
  if (!userId || !gridApi) return false;
  try {
    const colState: any[] = gridApi.getColumnState?.() || [];
    // ZA_USER_GRID.HIDE('Y'|'N')에 숨김여부를 그대로 저장 - 너비(WIDTH)는 숨김 여부와 무관하게
    // 실제 값을 보존해서, 나중에 컬럼선택기로 다시 켤 때 원래 폭 그대로 보이도록 함.
    // PINNED('left'|'right'|null)도 함께 저장 - GridHeaderPinToggle로 고정한 컬럼이 새로고침 후에도 유지됨.
    // no/cud(시스템 컬럼, colDef에 pinned:'left'+lockPinned:true로 항상 고정)는 getColumnState() 값과
    // 무관하게 무조건 'left'로 저장 - 복원 시(restoreGridLayout) 이 두 컬럼이 절대 고정 해제되지 않도록 보장.
    const columns = colState
      .filter(c => c?.colId && (c.width != null || c.hide || c.pinned))
      .map((c, idx) => ({
        objId: c.colId,
        xPos: idx,
        width: c.width != null ? Math.round(c.width) : 0,
        hide: c.hide ? 'Y' as const : 'N' as const,
        pinned: (c.colId === 'no' || c.colId === 'cud') ? 'left' as const : ((c.pinned === 'left' || c.pinned === 'right') ? c.pinned : null),
      }));
    const pageSize = gridApi.paginationGetPageSize?.();
    const pagination = gridApi.getGridOption?.('pagination');
    const pageSizeSelector = gridApi.getGridOption?.('paginationPageSizeSelector');
    await axios.post(`${API_BASE_URL}/com/apiUserGridLayoutSave`, { programId: callPgmId, gridId, data: { columns, pageSize, pagination, pageSizeSelector, menuId, asDefault } });
    savedLayoutByApi.set(gridApi, columns.map(c => ({ colId: c.objId, width: c.width || undefined, pinned: c.pinned })));
    return true;
  } catch (_) {
    return false;
  }
}

/////////////////////////////////////////////////////////////////////////////////////////////////////////
// function : resetGridLayout
// 설명     : ZA_USER_GRID에 저장된 컬럼 레이아웃(순서/너비)을 지우고 그리드를 기본 컬럼 정의로 되돌림
// 사용예시 : resetGridLayout(callPgmId, 'agGrid1', agGrid1.current?.api);
/////////////////////////////////////////////////////////////////////////////////////////////////////////
export async function resetGridLayout(callPgmId: string, gridId: string, gridApi: any, asDefault?: boolean): Promise<boolean> {
  const userId = getUserInfo()?.userId || '';
  try {
    if (userId) {
      await axios.post(`${API_BASE_URL}/com/apiUserGridLayoutDelete`, { programId: callPgmId, gridId, data: { asDefault } });
    }
  } catch (_) { /* 저장 실패해도 화면상 초기화는 진행 */ }
  // asDefault(전체 사용자 공통 기본값 삭제) 시에는 현재 보고 있는 사용자 자신의 화면은 되돌리지 않음.
  if (asDefault) return true;
  if (gridApi) savedLayoutByApi.delete(gridApi);
  // await 사이(서버 응답 대기 중)에 탭을 닫아 그리드가 destroy됐을 수 있음 - 그 상태에서 메서드를 부르면
  // "grid has been destroyed" 경고가 남(GridHeaderPinToggle과 같은 이유).
  if (!gridApi?.isDestroyed?.()) gridApi?.resetColumnState?.();
  return true;
}

/////////////////////////////////////////////////////////////////////////////////////////////////////////
// 행추가/행복사 시 임시 rowKey 채번 - 서버가 addRowNumber()로 내려주는 rowKey는 항상 조회결과 내 순번(양수,
// 1부터)이므로, 신규/복사행은 음수로 채번하면 서버 rowKey와 겹칠 확률이 아니라 구조적으로 절대 겹치지 않음.
// 세션 동안 계속 감소하는 모듈 스코프 카운터라 같은 화면 안 여러 그리드/여러 행에서도 서로 안 겹침.
// (기존엔 100000~999999 랜덤값을 썼으나, 이는 "서버 rowKey가 항상 그보다 작다"는 가정에 기댄 확률적 회피였음)
/////////////////////////////////////////////////////////////////////////////////////////////////////////
let tempKeySeq = 0;
const nextTempKey = () => --tempKeySeq;

/////////////////////////////////////////////////////////////////////////////////////////////////////////
// 그리드 "전체저장/전체취소"(우클릭 메뉴) 지원용 - 모듈 스코프 레지스트리
//  - AgGrid 컴포넌트는 자기 자신의 api만 알고, 같은 화면의 다른 그리드는 모름.
//  - 화면(callPgmId) 단위로 현재 마운트된 그리드들의 api를 등록해뒀다가, 우클릭 메뉴에서
//    "전체저장/전체취소"를 누르면 같은 programId로 등록된 그리드 전부를 순회 처리함.
/////////////////////////////////////////////////////////////////////////////////////////////////////////
const gridRegistry = new Map<string, Map<string, any>>();

function registerGrid(callPgmId?: string, gridId?: string, api?: any) {
  if (!callPgmId || !gridId || !api) return;
  if (!gridRegistry.has(callPgmId)) gridRegistry.set(callPgmId, new Map());
  gridRegistry.get(callPgmId)!.set(gridId, api);
}
function unregisterGrid(callPgmId?: string, gridId?: string) {
  if (!callPgmId || !gridId) return;
  gridRegistry.get(callPgmId)?.delete(gridId);
}
function getRegisteredGrids(callPgmId?: string): Array<[string, any]> {
  if (!callPgmId) return [];
  return Array.from(gridRegistry.get(callPgmId)?.entries() || []);
}

// 2026-09-11: gridApi -> agGridType("Q"|"I") 매핑. callPgmId/gridId 없이도(=레이아웃저장 미사용 그리드도) 항상 등록되어,
// setInsertRow/setDeleteRow/setCopyRow/setCancelRow가 "이 그리드가 조회전용(Q)인지" 판단하는 데 사용됨.
const gridTypeByApi = new WeakMap<any, "Q" | "I" | undefined>();
// 행취소용 "조회 당시 값" 저장소 - 그리드(api)별로 rowKey -> 미수정 상태(cud 비어 있음)의 마지막 행 값.
// 화면이 행마다 _origin을 붙이지 않아도 setCancelRow가 이 저장소로 복원함(행에 _origin이 있으면 그것을 우선 사용).
const originStoreByApi = new WeakMap<any, Map<any, any>>();
function isQueryOnlyGridApi(gridApi: any): boolean {
  return gridTypeByApi.get(gridApi) === 'Q';
}

/////////////////////////////////////////////////////////////////////////////////////////////////////////
// 2026-09-11: 페이지(탭)/브라우저 닫기 전 "작업중(cud 있음)" 그리드 체크 - gridRegistry를 그대로 재활용.
/////////////////////////////////////////////////////////////////////////////////////////////////////////
function hasUnsavedRows(api: any): boolean {
  let found = false;
  try {
    api?.forEachNode?.((node: any) => {
      if (!found && node?.data?.cud) found = true;
    });
  } catch (_) {
    // noop – 조회 실패 시 저장 안 된 자료 없음으로 간주
  }
  return found;
}
// 특정 화면(callPgmId)에 등록된 그리드 중 하나라도 작업중(cud 있음)인 행이 있는지 - 탭 닫기 전 체크용.
export function hasUnsavedGrid(callPgmId?: string): boolean {
  if (!callPgmId) return false;
  return getRegisteredGrids(callPgmId).some(([, api]) => hasUnsavedRows(api));
}
function hasAnyUnsavedGrid(): boolean {
  for (const grids of gridRegistry.values()) {
    for (const api of grids.values()) {
      if (hasUnsavedRows(api)) return true;
    }
  }
  return false;
}
// 브라우저 탭/창 닫기·새로고침 - 앱에 등록된 그리드 전체를 대상으로 하나라도 작업중이면 브라우저 기본 이탈경고 표시.
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', (e: BeforeUnloadEvent) => {
    if (hasAnyUnsavedGrid()) {
      e.preventDefault();
      e.returnValue = '';
    }
  });
}

// 2026-09-12: 그리드에 작업중(cud 있음)인 행이 있는 상태에서 조회를 누르면, 조회 결과로 그 내용이 덮어써져서
// 사라지므로 먼저 확인받음(NaviMenuBar의 조회 버튼에서 공통으로 사용) - 작업중인 게 없으면 확인 없이 바로 진행.
export async function confirmQueryDespiteUnsavedGrid(
  callPgmId: string,
  confirmMsgBox: (opts: any) => Promise<boolean>,
  ddKey: (key: string) => string
): Promise<boolean> {
  if (!hasUnsavedGrid(callPgmId)) return true;
  return await confirmMsgBox({
    title: ddKey('lblConfirm') || '확인',
    msgText: ddKey('msgQueryUnsavedConfirm') || '저장하지 않은 변경사항이 있습니다. 그래도 조회하시겠습니까?',
    showBtn: 'YesNo',
  });
}

/////////////////////////////////////////////////////////////////////////////////////////////////////////
// 클릭으로 선택된 그리드 파란 테두리 표시용 - 모듈 스코프 pub/sub
//  - 같은 화면(callPgmId) 안의 그리드 인스턴스들은 서로 독립된 React state라 다른 그리드가 클릭된 걸 알 방법이
//    없음. 그래서 programId별로 "마지막으로 클릭된 gridId"를 여기 모듈 스코프에 저장해두고, 바뀔 때마다 같은
//    programId를 구독 중인 모든 그리드 인스턴스를 강제로 리렌더시켜서 각자 "나만 활성인지"를 다시 계산하게 함.
//    (AG-Grid 내부 DOM 포커스에 의존하는 :focus-within 방식은 실제로 셀에 포커스가 안 걸리는 케이스가 있어
//     제외하고, 래퍼 div 자체의 mousedown 이벤트로 직접 처리함)
/////////////////////////////////////////////////////////////////////////////////////////////////////////
const activeGridByProgram = new Map<string, string>();
const activeGridListeners = new Map<string, Set<() => void>>();

function setActiveGrid(callPgmId: string, gridId: string) {
  if (activeGridByProgram.get(callPgmId) === gridId) return;
  activeGridByProgram.set(callPgmId, gridId);
  activeGridListeners.get(callPgmId)?.forEach(listener => listener());
}
function subscribeActiveGrid(callPgmId: string, listener: () => void): () => void {
  if (!activeGridListeners.has(callPgmId)) activeGridListeners.set(callPgmId, new Set());
  activeGridListeners.get(callPgmId)!.add(listener);
  return () => activeGridListeners.get(callPgmId)?.delete(listener);
}
// 화면(callPgmId)에서 지금 파란 테두리로 표시 중인 gridId 조회 - 툴바 명령(행추가/삭제/취소/복사 등)을
// 이 화면의 자체 activeAgGrid 로컬 state 대신 여기서 조회하도록 바꾸면, 데이터 없는 그리드를 클릭해
// 활성화한 경우(로컬 state는 못 따라가는 케이스)에도 명령이 항상 화면에 보이는 파란 테두리와 일치함.
export function getActiveGrid(callPgmId?: string): string | undefined {
  if (!callPgmId) return undefined;
  return activeGridByProgram.get(callPgmId);
}

  // console.log('saveZA2003M1G1Biz :',  data);
//////////////////////////////////////////////////////////////////////////////////////////////////////////
// 공통적인 컬럼을 정의함.
// - CUD : 행 상태 표시 (C: 생성, U: 수정, D: 삭제) --> 그리드타입 : I(입력)일때만 표시
// - No  : 행 번호 (1부터 시작)
// - RowKey : 행의 고유 키 (숨김) - 자동생성  --> 그리드타입 : I(입력)일때만 표시
// - (2026-09-28) 저장 사용자 아이디(login_user_id) 컬럼은 없앰 - 입력자/수정자는 서버가 인증토큰 사용자로 기록함
///////////////////////////////////////////////////////////////////////////////////////////////////////////
// 그리드타입 : Q(조회)일때만 표시
type preQueryColumnsType = {
  no: string;
};
const preQueryColumns: ColDef<preQueryColumnsType>[] = [
  { headerName: '', field: 'no', width: 59, minWidth: 50, maxWidth: 200, editable: false,
    sortable: false, resizable: true, suppressMovable: true, filter: false,
    pinned: 'left', lockPinned: true, // 좌측 상단 톱니(컬럼선택기) 버튼과 겹치는 자리 - 스크롤해도 항상 보이도록 고정(해제 불가), 너비 조정은 허용
    headerClass: ['has-icon-no','ag-header-center'],
    cellClass: ['ag-cell-text-align-center']
  },
];
// 그리드타입 : I(입력)일때만 표시
type preSaveColumnsType = {
  cud: string;
  no: string;
};
const preSaveColumns: ColDef<preSaveColumnsType>[] = [
  { headerName: '', field: 'cud', width: 20, editable: false,
    sortable: false, resizable: false, suppressMovable: true, filter: false,
    pinned: 'left', lockPinned: true, // 좌측 상단 톱니(컬럼선택기) 버튼과 겹치는 자리 - 스크롤해도 항상 보이도록 고정(해제 불가)
    hide: true, // 화면엔 안 보이지만 데이터(cud 필드)는 그대로 유지 - 저장 라우팅(C/U/D)·행 전체 색상표시(rowClassRules)는 컬럼 숨김과 무관하게 계속 동작함
    headerClass: ['has-icon-cud','header-padding-zero','ag-header-center'],
    cellClass: ['cell-padding-zero','ag-cell-text-align-center'],
    cellClassRules: { 'row-created': (params: any) => params.value === 'C',
                      'row-updated': (params: any) => params.value === 'U',
                      'row-deleted': (params: any) => params.value === 'D' }
  },
  { headerName: '', field: 'no', width: 59, minWidth: 50, maxWidth: 200, editable: false,
    sortable: false, resizable: true, suppressMovable: true, filter: false, // 위치는 고정, 너비 조정은 허용
    pinned: 'left', lockPinned: true,
    headerClass: 'ag-header-center',
    cellClass: ['ag-cell-text-align-center'],
    // cud 컬럼을 숨긴 대신, 신규/수정/삭제 행은 No 자리에 번호 없이 상태(C/U/D)만 표기
    valueFormatter: (params: any) => {
      const cud = params.data?.cud;
      if (cud === 'C' || cud === 'U' || cud === 'D') return cud;
      return params.value;
    }
  },
];
// 그리드타입 : I(입력)일때만 표시
type sysSaveColumnsType = {
  rowKey: string;
  // other fields...
};
const sysSaveColumns: ColDef<sysSaveColumnsType>[] = [
  { headerName: 'Key', field: 'rowKey', width: 80, hide: true,
     valueFormatter: params => params.value || '' 
  },
];
/////////////////////////////////////////////////////////////////////////////////////////////////////////
// component : GridHeaderPinToggle
// 설명   : 헤더 innerHeaderComponent - 컬럼명 옆에 고정핀 아이콘을 추가해서 클릭 시 해당 컬럼을 좌측 고정/해제.
//          (ag-Grid 기본 헤더의 정렬/필터/메뉴/리사이즈는 그대로 유지되고, 라벨 영역만 교체됨)
//          고정 상태 자체는 클릭 즉시 DB에 저장되지 않음(다른 컬럼 너비/순서 변경과 동일하게) - 화면에서
//          saveGridLayout()(그리드위치저장)을 호출해야 ZA_USER_GRID.PINNED로 영구 반영됨.
/////////////////////////////////////////////////////////////////////////////////////////////////////////
const GridHeaderPinToggle: React.FC<any> = (params) => {
  const { column, api, displayName } = params;
  const { ddKey } = useCommonTranslation();
  const [pinned, setPinned] = useState<'left' | 'right' | null>(() => column?.getPinned?.() ?? null);

  useEffect(() => {
    const handler = () => setPinned(column?.getPinned?.() ?? null);
    api?.addEventListener?.('columnPinned', handler);
    // 2026-09-28: 탭/화면을 닫아서 그리드 자체가 먼저 destroy된 뒤에 이 컴포넌트의 정리(cleanup)가 늦게
    // 실행되면, 이미 죽은 api에 removeEventListener를 불러서 "warning #26 grid has been destroyed"가 남 -
    // ag-Grid 권장대로 isDestroyed()로 살아있을 때만 호출.
    return () => { if (!api?.isDestroyed?.()) api?.removeEventListener?.('columnPinned', handler); };
  }, [column, api]);

  const togglePin = useCallback((e: React.MouseEvent) => {
    e.stopPropagation(); // 헤더 클릭(정렬)로 이벤트가 번지지 않도록
    api?.setColumnsPinned?.([column], pinned ? null : 'left');
  }, [api, column, pinned]);

  // No/CUD처럼 항상 고정이라 사용자가 해제하면 안 되는 컬럼(colDef에 lockPinned:true)은 아이콘 자체를 표시하지 않음
  const isLockedPin = !!column?.getColDef?.()?.lockPinned;
  if (isLockedPin) {
    return <span className="aggrid-header-pin-label">{displayName}</span>;
  }

  return (
    <span className="aggrid-header-pin-wrapper">
      <span className="aggrid-header-pin-label">{displayName}</span>
      <i
        className={`fas fa-thumbtack aggrid-header-pin-icon ${pinned ? 'pinned' : ''}`}
        onClick={togglePin}
        title={pinned ? (ddKey('unpinColumn') || '고정 해제') : (ddKey('pinColumnLeft') || '왼쪽 고정')}
      ></i>
    </span>
  );
};

/////////////////////////////////////////////////////////////////////////////////////////////////////////
// component : AgGrid
/////////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////////
// 컬럼 그룹핑(조회전용 그리드) - ag-Grid Community에는 행 그룹핑(Enterprise)이 없어서 직접 구현함.
//  - 조회된 행(agGridRows)을 선택한 컬럼 값별로 묶어 [그룹헤더행 + 소속행들] 순서로 펼쳐서 rowData로 넘김.
//  - 그룹헤더행은 전체폭 행(isFullWidthRow)으로 그리고, 클릭하면 접기/펼치기(행 클릭 선택은 막음).
//  - 그룹행은 rowData에 __group:true로 섞여 들어가므로, 화면의 선택/클릭 이벤트에는 전달하지 않음(mergedEventHandlers).
//  - 그룹핑 중에는 페이징을 끄고 전체를 스크롤로 보여줌(그룹이 페이지 경계에서 잘리지 않게).
/////////////////////////////////////////////////////////////////////////////////////////////////////////
//  - 2026-09-24: 다단계 그룹핑(그룹 안의 그룹) + 소계/합계 행 추가.
//      . 그룹은 [그룹헤더 -> (하위그룹 또는 소속행) -> 소계행] 순서, 맨 끝에 전체 합계행.
//      . 소계/합계는 type:'numericColumn' 컬럼(colDef.noSum:true면 제외)을 합산하며, 일반 행(__subtotal:true)으로 들어가
//        기본 셀렌더러(cellRendererSelector)가 라벨/합계값만 표시함. 엑셀/CSV 내보내기에는 소계/합계도 포함됨.
//  - 2026-09-24: 범위 선택 복사 - 셀을 드래그하면 사각형 범위가 표시되고 Ctrl+C로 탭구분(TSV) 복사(엑셀에 그대로 붙여넣기).
const GroupRow = (props: any) => {
  const { data, onToggle } = props;
  return (
    <div
      className="aggrid-group-row-cell"
      style={{ paddingLeft: 12 + (data.__level || 0) * 20 }}
      onClick={(e) => { e.stopPropagation(); onToggle?.(data.__key); }}
    >
      <i className={`fas ${data.__collapsed ? 'fa-caret-right' : 'fa-caret-down'}`} />
      <span className="aggrid-group-title">{data.__title}</span>
      <span className="aggrid-group-count">{data.__count}건</span>
    </div>
  );
};
const GROUP_EXCLUDE_FIELDS = ['no', 'cud', 'rowKey', 'login_user_id'];
const GROUP_EMPTY_LABEL = '(없음)';
const GROUP_KEY_SEP = '\u0001';   // 다단계 그룹 경로 구분자(그룹키 = 상위값 + SEP + 하위값)
const isSynthRow = (d: any) => !!(d?.__group || d?.__subtotal);   // 그룹헤더/소계/합계 = 화면 이벤트로 전달하지 않는 가짜 행

// 2026-10-05: popupOnly 컬럼의 셀 오른쪽 팝업 아이콘 - 누르면 셀 더블클릭과 같은 처리(onOpen)
//  - ag-Grid는 그리드 루트에서 네이티브 이벤트를 받으므로(React의 stopPropagation은 그보다 늦음) 아이콘 자체에
//    네이티브 리스너를 걸어 mousedown/click/dblclick이 셀 편집·행 선택으로 번지지 않게 막음
const PopupCellIcon: React.FC<{ onOpen: (ev: MouseEvent) => void }> = ({ onOpen }) => {
  const ref = useRef<HTMLSpanElement>(null);
  const openRef = useRef(onOpen);
  openRef.current = onOpen;
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const stop = (e: Event) => { e.stopPropagation(); e.preventDefault(); };
    const click = (e: MouseEvent) => { stop(e); openRef.current(e); };
    el.addEventListener('mousedown', stop);
    el.addEventListener('dblclick', stop);
    el.addEventListener('click', click);
    return () => { el.removeEventListener('mousedown', stop); el.removeEventListener('dblclick', stop); el.removeEventListener('click', click); };
  }, []);
  // 셀(.ag-cell, position:absolute) 기준으로 오른쪽 끝에 붙이고 위아래로 늘려 행 높이에 맞춤 - 감싸는 div는 위치기준을 만들지 않음
  return (
    <span ref={ref} title="팝업에서 선택"
      style={{ position: 'absolute', top: 1, bottom: 1, right: 1, width: 24, display: 'flex', alignItems: 'center', justifyContent: 'center',
        border: '1px solid #cbd5e1', borderRadius: 3, background: 'transparent', boxSizing: 'border-box', cursor: 'pointer', lineHeight: 1 }}>
      <i className="fas fa-magnifying-glass" style={{ fontSize: 11, color: '#64748b' }} />
    </span>
  );
};
// 엑셀 등에서 복사하면 끝에 개행이 하나 더 붙어 나오는 관례를 제거함(딱 1개만) - 붙여넣기(Ctrl+V)에서 사용.
// while로 끝 빈 줄을 계속 지우면 "빈 셀 하나만 복사"(클립보드가 그냥 "")한 경우까지 통째로 지워짐.
const stripOneTrailingNewline = (text: string): string => {
  const normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  return normalized.endsWith('\n') ? normalized.slice(0, -1) : normalized;
};

// 소계/합계 행의 셀 표시 - 라벨 컬럼엔 '소계'/'합계', 합산 컬럼엔 합계값(컬럼의 valueFormatter 적용), 나머지는 빈칸
const SubtotalCell = (props: any) => {
  const d = props.data || {};
  const f = props.colDef?.field;
  if (f && f === d.__labelField) return <span className="aggrid-subtotal-label">{d.__label}</span>;
  if (f && Array.isArray(d.__sumFields) && d.__sumFields.includes(f)) return <span>{props.valueFormatted ?? props.value ?? ''}</span>;
  return null;
};
const subtotalCellRendererSelector = (p: any) => (p?.data?.__subtotal ? { component: SubtotalCell } : undefined);

// 소계/합계용 숫자 변환(콤마 문자열 허용, 숫자가 아니면 null)
const toSumNumber = (v: any): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : Number(String(v).replace(/,/g, ''));
  return Number.isFinite(n) ? n : null;
};

const AgGrid = forwardRef(<T extends { }>(
  {
    agGridType = { type: "Q" },
    agGrid,
    agGridRows,
    agGridCols,
    agGridColsDefault,
    loading = false,
    pagination = true,
    pageSize = 20,
    pageSizeSelector = [10, 20, 50, 100, 500, 1000],
    rowSelection = { type: "multiple" },   // 2026-09-24: 기본을 다중선택으로 변경(Ctrl/Shift+클릭) - 단일선택이 필요한 화면만 { type: "single" } 지정
    suppressRowClickSelection = false,
    height = "65vh",
    rowHeight = 30,
    eventHandlers = {},
    callPgmId,
    gridId,
    rowClassRules: extraRowClassRules,
    getRowStyle,
    setAgGridRows,
    menuId,
  }: AgGridProps<T>,
  _ref: React.ForwardedRef<AgGridReact<T>>
) => {
    /////////////////////////////////////////////////////////////////////////////////////////////////////////
    // 0. 공통정의
    /////////////////////////////////////////////////////////////////////////////////////////////////////////
    const { ddKey, language } = useCommonTranslation();    // 공통라벨 번역
    const { showRefPopup } = useRefPopupModal();    // 우클릭메뉴 [레퍼런스팝업 열기]용
    const { confirmMsgBox, showMsgBox } = useMsgBoxModal();    // confirmMsgBox: 붙여넣을 블록이 선택범위보다 클 때 확인용 / showMsgBox: 그리드위치(A)의 [컬럼속성보기]용

  const internalRef = useRef<AgGridReact<T>>(null);
  const agGridRef = agGrid ?? internalRef;
  // pageSizeSelector를 화면에서 JSX 배열 리터럴로 넘기면(예: pageSizeSelector={[100, 500, 1000, 2000, 3000, 5000]})
  // 매 렌더마다 새 배열 참조가 생겨서, ag-grid-react가 그걸 "바뀐 prop"으로 보고 리렌더마다 다시 밀어넣음 ->
  // restoreGridLayout()이 복원한 페이지 크기/페이지네이션이 그 직후 리렌더에서 원래 prop 값으로 되돌아가버림
  // (2026-10-07 발견). 내용이 같으면 같은 참조를 쓰도록 고정해서 불필요한 재적용을 막음.
  const pageSizeSelectorKey = JSON.stringify(pageSizeSelector);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stablePageSizeSelector = useMemo(() => pageSizeSelector, [pageSizeSelectorKey]);
  // 행취소용 조회 당시 값 - 수정/신규 표시(cud)가 없는 행의 값을 계속 기록해 두면(조회/저장 후 재조회/행취소로 되돌아온 상태),
  // 이후 수정된 행은 마지막으로 기록된 미수정 값이 곧 조회 당시 값이 됨
  const originStoreRef = useRef<Map<any, any>>(new Map());
  useEffect(() => {
    const store = originStoreRef.current;
    for (const r of (agGridRows as any[]) || []) {
      if (r && r.rowKey !== undefined && !r.cud && !r.__group && !r.__subtotal) { const { _origin, ...snap } = r; store.set(r.rowKey, snap); }
    }
  }, [agGridRows]);

  /////////////////////////////////////////////////////////////////////////////////////////////////////////
  // 활성 그리드(파란 테두리) 표시 - "지금 툴바 명령(행추가/삭제/취소/복사 등)이 적용될 그리드"를 나타내는 용도.
  //   - 오직 사용자의 실제 마우스 클릭(래퍼 div의 onMouseDownCapture)만 활성화 트리거로 삼음.
  //   - 한때 AG-Grid의 onCellFocused도 같이 썼었는데, 종속관계인 그리드(부모 행 클릭 -> 자식 그리드
  //     연쇄조회 -> selectFirstRow가 자식 그리드에 setFocusedCell 호출)에서는 사용자가 그리드1을 클릭한
  //     직후 연쇄조회가 그리드2/3까지 자동으로 포커스를 끌고가버려서, 정작 그리드1에 행추가를 하려 해도
  //     활성이 그리드3으로 튕겨나가는 문제가 있어 제거함 - 반드시 "직접 클릭한" 그리드만 활성화되어야 함.
  // 같은 화면(callPgmId)의 다른 그리드가 클릭되면 리렌더되어 이 그리드의 테두리는 자동 해제됨.
  /////////////////////////////////////////////////////////////////////////////////////////////////////////
  const [, forceActiveGridUpdate] = useReducer((c: number) => c + 1, 0);
  useEffect(() => {
    if (!callPgmId || !gridId) return;
    return subscribeActiveGrid(callPgmId, () => forceActiveGridUpdate());
  }, [callPgmId, gridId]);
  // 화면이 처음 열렸을 때(아직 아무도 클릭 안 한 상태) agGrid1이 있으면 기본으로 활성표시 - 화면단 수정 불필요
  useEffect(() => {
    if (!callPgmId || !gridId) return;
    if (gridId === 'agGrid1' && !activeGridByProgram.has(callPgmId)) {
      setActiveGrid(callPgmId, gridId);
    }
  }, [callPgmId, gridId]);
  const isActiveGrid = !!callPgmId && !!gridId && activeGridByProgram.get(callPgmId) === gridId;
  const handleGridMouseDown = useCallback(() => {
    if (callPgmId && gridId) setActiveGrid(callPgmId, gridId);
  }, [callPgmId, gridId]);

  /////////////////////////////////////////////////////////////////////////////////////////////////////////
  // 그리드 우클릭 메뉴 - 브라우저 기본 메뉴 대신 표시.
  //   - 빈 공간/헤더 우클릭 : 컨테이너 div의 handleContextMenu(필드정보 없음, 그리드위치저장/취소만 표시)
  //   - 셀 위 우클릭 : ag-Grid의 onCellContextMenu(handleCellContextMenu)가 필드/값까지 담아서 처리하고,
  //     stopPropagation으로 컨테이너 핸들러로 안 번지게 막아 이중처리를 방지함.
  /////////////////////////////////////////////////////////////////////////////////////////////////////////
  const [contextMenu, setContextMenu] = useState<{ mouseX: number; mouseY: number; field?: string; value?: any; rowData?: any; rowIndex?: number; groupCol?: string } | null>(null);
  // "그리드위치(A)" 메뉴 노출 여부 - ZA_USER.USER_KIND='SA'(시스템마스터) 계정만 true (2026-10-07)
  const isSysAdmin = getUserInfo()?.userKind === 'SA';
  // "그리드위치"/"그리드위치(A)" 하위메뉴 - 순수 CSS(:hover)로 표시/숨김(2026-10-08, 별도 Popover/상태 없음.
  // 이유는 아래 gridPosFlyoutSx 선언부 주석 참고)
  const gridPosFlyoutSx = {
    display: 'none',
    position: 'absolute' as const,
    top: 0,
    // 트리거 항목과 틈 없이 약간 겹치게(음수 여백) - 틈이 있으면 마우스가 트리거 오른쪽 끝을 벗어나
    // 플라이아웃에 닿기 전 그 틈 구간에서 둘 다 :hover가 아니게 되어 바로 닫혀버림(실제 Playwright로
    // 단계적 마우스 이동 재현해서 확인한 문제, 2026-10-08) - 겹치는 구간을 둬서 항상 둘 중 하나는 hover 상태 유지.
    left: 'calc(100% - 3px)',
    minWidth: 190,
    borderRadius: 2,
    boxShadow: '0 8px 24px rgba(0,0,0,0.16)',
    bgcolor: 'background.paper',
    p: 0.5,
    zIndex: 1400,
  };

  // 컬럼 그룹핑(조회전용 그리드만) - groupField: 그룹 기준 컬럼(null이면 해제), collapsedGroups: 접힌 그룹키
  //   2026-09-24: 다단계 - groupFields: 그룹 기준 컬럼 목록(앞이 상위 단계, 빈 배열이면 해제), collapsedGroups: 접힌 그룹 경로키
  const [groupFields, setGroupFields] = useState<string[]>([]);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const groupActiveRef = useRef(false);
  const findColDef = useCallback((field?: string | null): any => {
    if (!field) return undefined;
    const walk = (cols: any[]): any => {
      for (const c of cols || []) {
        if (c?.children) { const r = walk(c.children); if (r) return r; }
        else if (c?.field === field) return c;
      }
      return undefined;
    };
    return walk(agGridCols as any[]);
  }, [agGridCols]);
  const groupedRows = useMemo(() => {
    if (!groupFields.length || agGridType?.type !== 'Q') return null;
    const rows = ((agGridRows as any[]) || []).filter(r => !isSynthRow(r));
    if (rows.length === 0) return null;
    const defs = groupFields.map(f => findColDef(f));
    if (defs.some(d => !d)) return null;
    const labelOf = (row: any, level: number): string => {
      const colDef = defs[level];
      const raw = row?.[groupFields[level]];
      let v: any = raw;
      if (typeof colDef.valueFormatter === 'function') {
        try { v = colDef.valueFormatter({ value: raw, data: row, colDef, node: null, api: agGridRef.current?.api, column: null, context: undefined }); } catch { v = raw; }
      }
      return v == null || String(v).trim() === '' ? GROUP_EMPTY_LABEL : String(v);
    };
    // 소계/합계 대상 컬럼 = numericColumn 타입(noSum/숨김/공통컬럼/그룹기준 컬럼 제외), 라벨 표시 컬럼 = 첫번째 일반(비숫자) 컬럼
    const leafDefs: any[] = [];
    const walk = (cols: any[]) => (cols || []).forEach(c => { if (c?.children) walk(c.children); else if (c?.field) leafDefs.push(c); });
    walk(agGridCols as any[]);
    const isNumeric = (c: any) => c.type === 'numericColumn' || (Array.isArray(c.type) && c.type.includes('numericColumn'));
    const sumFields: string[] = leafDefs.filter(c => isNumeric(c) && !c.noSum && !c.hide && !GROUP_EXCLUDE_FIELDS.includes(c.field) && !groupFields.includes(c.field)).map(c => c.field);
    const labelCands = leafDefs.filter(c => !c.hide && !isNumeric(c) && !GROUP_EXCLUDE_FIELDS.includes(c.field));
    const labelField: string | undefined = (labelCands.find(c => !c.valueFormatter && !c.cellRenderer) || labelCands[0])?.field;
    const hasSum = sumFields.length > 0 && !!labelField;
    const subtotalOf = (list: any[], key: string, label: string, level: number) => {
      const sums: Record<string, number> = {};
      sumFields.forEach(f => { let t = 0; list.forEach(r => { const n = toSumNumber(r?.[f]); if (n !== null) t += n; }); sums[f] = t; });
      return { ...sums, __subtotal: true, __key: key, __label: label, __labelField: labelField, __sumFields: sumFields, __level: level, [labelField as string]: label };
    };
    const subLabel = ddKey('lblSubtotal') || '소계';
    const totalLabel = ddKey('lblTotal') || '합계';
    const cmp = (a: string, b: string) => {
      if (a === GROUP_EMPTY_LABEL) return 1;
      if (b === GROUP_EMPTY_LABEL) return -1;
      return a.localeCompare(b, undefined, { numeric: true });
    };
    const out: any[] = [];
    const build = (list: any[], level: number, path: string) => {
      const groups = new Map<string, any[]>();
      list.forEach(r => { const k = labelOf(r, level); if (!groups.has(k)) groups.set(k, []); groups.get(k)!.push(r); });
      const title = defs[level].headerName || groupFields[level];
      Array.from(groups.keys()).sort(cmp).forEach(k => {
        const sub = groups.get(k)!;
        const p = path ? path + GROUP_KEY_SEP + k : k;
        const collapsed = collapsedGroups.has(p);
        out.push({ __group: true, __key: p, __level: level, __title: `${title} : ${k}`, __count: sub.length, __collapsed: collapsed });
        if (!collapsed) {
          if (level + 1 < groupFields.length) build(sub, level + 1, p);
          else out.push(...sub);
        }
        if (hasSum) out.push(subtotalOf(sub, `s:${p}`, `${subLabel} (${k})`, level));   // 접힌 그룹도 소계는 계속 보여줌
      });
    };
    build(rows, 0, '');
    if (hasSum) out.push(subtotalOf(rows, '__total', totalLabel, 0));
    return out;
  }, [groupFields, collapsedGroups, agGridRows, agGridCols, agGridType?.type, findColDef, agGridRef, ddKey]);
  groupActiveRef.current = !!groupedRows;
  const handleToggleGroup = useCallback((key: string) => {
    setCollapsedGroups(prev => { const n = new Set(prev); if (n.has(key)) n.delete(key); else n.add(key); return n; });
  }, []);
  const fullWidthCellRendererParams = useMemo(() => ({ onToggle: handleToggleGroup }), [handleToggleGroup]);

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setRangeTip(null);   // 우클릭하면 합계/개수 툴팁 숨김(같은 집계가 우클릭 메뉴에 표시됨) - 범위 선택 자체는 유지
    // No(순번) 컬럼의 "헤더"를 우클릭한 경우도 완전히 무반응이어야 함 - 데이터 셀은 onCellContextMenu
    // (handleCellContextMenu)가 stopPropagation으로 여기까지 안 오게 막지만, 헤더 우클릭은 ag-Grid에
    // 별도 콜백이 없어 컨테이너의 이 핸들러로 바로 들어오므로 col-id 속성으로 직접 판별함.
    const target = e.target as HTMLElement;
    if (target?.closest?.('.ag-header-cell[col-id="no"]')) return;
    const hdrColId = (target?.closest?.('.ag-header-cell') as HTMLElement | null)?.getAttribute('col-id') || undefined;
    setContextMenu(contextMenu ? null : { mouseX: e.clientX - 2, mouseY: e.clientY - 4, groupCol: hdrColId });
  }, [contextMenu]);

  const handleCloseContextMenu = useCallback(() => { setContextMenu(null); }, []);

  /////////////////////////////////////////////////////////////////////////////////////////////////////////
  // "그리드위치(A)" > [컬럼속성보기] - 우클릭한 컬럼(셀 우클릭이면 contextMenu.field, 헤더 우클릭이면
  // contextMenu.groupCol)의 정의를 간단한 메시지박스로 보여줌(관리자 디버깅용, 2026-10-10).
  //  - ag-Grid Column API(getColumn().getColDef())로 조회해서 defaultColDef(agGridColsDefault)가 적용된
  //    "실제" 값을 보여줌(화면 코드의 colDef 원본만 보면 개별 지정 안 한 editable/filter 등이 안 보임).
  //    API로 못 찾으면(드문 경우) 화면이 선언한 원본 colDef(findColDef)로 대체.
  /////////////////////////////////////////////////////////////////////////////////////////////////////////
  const handleContextShowColumnInfo = useCallback(() => {
    const field = contextMenu?.field || contextMenu?.groupCol;
    handleCloseContextMenu();
    if (!field) return;
    const col = agGridRef.current?.api?.getColumn?.(field);
    const colDef: any = col?.getColDef?.() || findColDef(field) || {};
    const typeLabel = Array.isArray(colDef.type) ? colDef.type.join(', ') : (colDef.type || '일반(문자)');
    const isPk = !!(colDef.context?.pk ?? colDef.pk);
    const isRequired = !!(colDef.context?.required ?? colDef.required);
    const width = col?.getActualWidth?.() ?? colDef.width;
    const lines = [
      `컬럼ID : ${colDef.field || colDef.colId || field}`,
      `컬럼명 : ${colDef.headerName || ''}`,
      `타입 : ${typeLabel}`,
      `폭(px) : ${width ?? ''}`,
      `정렬 : ${colDef.context?.align || '-'}`,
      `PK여부 : ${isPk ? 'Y' : 'N'}`,
      `필수여부 : ${isRequired ? 'Y' : 'N'}`,
      `수정가능 : ${colDef.editable ? 'Y' : 'N'}`,
      `숨김여부 : ${colDef.hide ? 'Y' : 'N'}`,
      `필터사용 : ${colDef.filter === false ? 'N' : 'Y'}`,
      `정렬사용 : ${colDef.sortable === false ? 'N' : 'Y'}`,
    ];
    if (colDef.context?.dateType) lines.push(`날짜형식 : ${colDef.context.dateType}`);
    if (colDef.cellEditor) lines.push(`에디터 : ${typeof colDef.cellEditor === 'string' ? colDef.cellEditor : '사용자정의'}`);
    if (colDef.valueFormatter) lines.push(`값포맷터 : 있음`);
    if (colDef.cellRenderer) lines.push(`셀렌더러 : ${typeof colDef.cellRenderer === 'string' ? colDef.cellRenderer : '사용자정의'}`);
    showMsgBox({ title: ddKey('lblColumnInfo') || '컬럼 속성', msgText: lines.join('\n'), showBtn: 'OK', msgType: 'info' });
  }, [contextMenu, agGridRef, findColDef, handleCloseContextMenu, showMsgBox, ddKey]);

  /////////////////////////////////////////////////////////////////////////////////////////////////////////
  // 우클릭메뉴 [OOO 팝업 열기] - 컬럼(field)별 레퍼런스팝업 매핑(ZA_GRID_RMENU)을 첫 우클릭 시 1회만
  // 조회해서 캐싱해두고 재사용함(지연로딩+메모이제이션 - 우클릭 메뉴는 즉시 떠야 하는 UI라서 앱 시작
  // 시점에 미리 불러오지 않음).
  /////////////////////////////////////////////////////////////////////////////////////////////////////////
  const [gridRMenuRows, setGridRMenuRows] = useState<GridRMenuRow[]>([]);
  const matchedRMenu = useMemo(
    () => findGridRMenu(gridRMenuRows, menuId || pgmIdOf(callPgmId), contextMenu?.field),
    [gridRMenuRows, menuId, callPgmId, contextMenu?.field]
  );

  const handleCellContextMenu = useCallback((params: any) => {
    const native = params?.event as MouseEvent | undefined;
    if (!native) return;
    setRangeTip(null);   // 우클릭하면 합계/개수 툴팁 숨김(같은 집계가 우클릭 메뉴에 표시됨) - 범위 선택 자체는 유지
    native.preventDefault();
    native.stopPropagation(); // 컨테이너 div의 handleContextMenu로 번지지 않게 막아 이중처리 방지
    // No(순번) 컬럼은 필드별 팝업/값 관련 메뉴가 의미없으므로 우클릭 메뉴 자체를 띄우지 않음
    // (네이티브 브라우저 메뉴는 위에서 이미 preventDefault로 막았으므로 완전히 아무 반응 없음).
    if (params.colDef?.field === 'no') return;
    setContextMenu({ mouseX: native.clientX - 2, mouseY: native.clientY - 4, field: params.colDef?.field, value: params.value, rowData: params.data, rowIndex: params.node?.rowIndex ?? undefined, groupCol: params.colDef?.field });
    loadGridRMenu().then(setGridRMenuRows); // 최초 1회만 실제 조회, 이후엔 캐시된 값을 즉시 반환
    eventHandlers?.onCellContextMenu?.(params);
  }, [eventHandlers]);

  const handleOpenRefPopup = useCallback(() => {
    if (!matchedRMenu) return;
    const { popupUrl, paramKey, titleKey, objId } = matchedRMenu;
    // PARAM_KEY는 "a,b" 또는 "팝업파라미터=그리드컬럼"(쉼표 구분) - 각 그리드컬럼 값을 같은 행(rowData)에서 가져옴.
    // 단일 키일 때만 행에 값이 없으면 우클릭한 셀 자신의 값으로 대체(기존 동작 유지).
    const pairs = parseParamPairs(paramKey, objId);
    const paramData: Record<string, any> = {};
    pairs.forEach(({ param, field }) => {
      const fromRow = contextMenu?.rowData?.[field];
      paramData[param] = fromRow ?? (pairs.length === 1 ? contextMenu?.value : undefined) ?? '';
    });
    handleCloseContextMenu();
    showRefPopup({
      title: (titleKey && ddKey(titleKey)) || matchedRMenu.objId,
      callComponent: resolveComponent(popupUrl),
      pgmId: pgmIdOf(popupUrl),   // lazy 컴포넌트라 static pgmId를 못 읽음 - 권한 확인용 프로그램ID를 직접 넘김
      paramProps: { paramData },
      modalHeight: '90%',
      modalWidth: '70%',
      autoQuery: true,
    });
  }, [matchedRMenu, contextMenu, ddKey, showRefPopup, handleCloseContextMenu]);

  // 우클릭 메뉴 - 그리드위치저장/취소(이 그리드만)
  const handleContextSaveGrid = useCallback(async () => {
    handleCloseContextMenu();
    const api = agGridRef.current?.api;
    if (callPgmId && gridId && api) await saveGridLayout(callPgmId, gridId, api, menuId || pgmIdOf(callPgmId));
  }, [callPgmId, gridId, agGridRef, menuId]);

  const handleContextResetGrid = useCallback(async () => {
    handleCloseContextMenu();
    const api = agGridRef.current?.api;
    if (callPgmId && gridId) await resetGridLayout(callPgmId, gridId, api);
  }, [callPgmId, gridId, agGridRef]);

  // 우클릭 메뉴 - 전체그리드위치저장/취소(같은 화면(callPgmId)에 등록된 모든 그리드)
  //  - 레지스트리(getRegisteredGrids)에 이 그리드 자신이 아직(또는 어떤 이유로) 없더라도, 최소한
  //    지금 우클릭한 이 그리드만큼은 "저장/초기화(이 그리드만)"와 동일하게 항상 처리되도록 자기 자신을 보장 포함.
  const handleContextSaveGridAll = useCallback(async () => {
    handleCloseContextMenu();
    if (!callPgmId) return;
    const grids = getRegisteredGrids(callPgmId);
    const selfApi = agGridRef.current?.api;
    const allGrids = (gridId && selfApi && !grids.some(([gid]) => gid === gridId)) ? [...grids, [gridId, selfApi] as [string, any]] : grids;
    await Promise.all(allGrids.map(([gid, api]) => saveGridLayout(callPgmId, gid, api, menuId || pgmIdOf(callPgmId))));
  }, [callPgmId, gridId, agGridRef, menuId]);

  const handleContextResetGridAll = useCallback(async () => {
    handleCloseContextMenu();
    if (!callPgmId) return;
    const grids = getRegisteredGrids(callPgmId);
    const selfApi = agGridRef.current?.api;
    const allGrids = (gridId && selfApi && !grids.some(([gid]) => gid === gridId)) ? [...grids, [gridId, selfApi] as [string, any]] : grids;
    await Promise.all(allGrids.map(([gid, api]) => resetGridLayout(callPgmId, gid, api)));
  }, [callPgmId, gridId, agGridRef]);

  // 우클릭 메뉴 - 그리드위치(A): 관리자용, USER_ID='*'(전체 공통 기본값)으로 저장/취소
  const handleContextSaveGridAdmin = useCallback(async () => {
    handleCloseContextMenu();
    const api = agGridRef.current?.api;
    if (callPgmId && gridId && api) await saveGridLayout(callPgmId, gridId, api, menuId || pgmIdOf(callPgmId), true);
  }, [callPgmId, gridId, agGridRef, menuId]);

  const handleContextResetGridAdmin = useCallback(async () => {
    handleCloseContextMenu();
    const api = agGridRef.current?.api;
    if (callPgmId && gridId) await resetGridLayout(callPgmId, gridId, api, true);
  }, [callPgmId, gridId, agGridRef]);

  const handleContextSaveGridAllAdmin = useCallback(async () => {
    handleCloseContextMenu();
    if (!callPgmId) return;
    const grids = getRegisteredGrids(callPgmId);
    const selfApi = agGridRef.current?.api;
    const allGrids = (gridId && selfApi && !grids.some(([gid]) => gid === gridId)) ? [...grids, [gridId, selfApi] as [string, any]] : grids;
    await Promise.all(allGrids.map(([gid, api]) => saveGridLayout(callPgmId, gid, api, menuId || pgmIdOf(callPgmId), true)));
  }, [callPgmId, gridId, agGridRef, menuId]);

  const handleContextResetGridAllAdmin = useCallback(async () => {
    handleCloseContextMenu();
    if (!callPgmId) return;
    const grids = getRegisteredGrids(callPgmId);
    const selfApi = agGridRef.current?.api;
    const allGrids = (gridId && selfApi && !grids.some(([gid]) => gid === gridId)) ? [...grids, [gridId, selfApi] as [string, any]] : grids;
    await Promise.all(allGrids.map(([gid, api]) => resetGridLayout(callPgmId, gid, api, true)));
  }, [callPgmId, gridId, agGridRef]);

  // 우클릭 메뉴 - 필터지우기(우클릭한 셀의 컬럼 필터만) / 필터지우기(전체, 그리드의 모든 컬럼 필터)
  const handleContextClearColumnFilter = useCallback(() => {
    const field = contextMenu?.field;
    handleCloseContextMenu();
    const api = agGridRef.current?.api;
    if (!api || !field) return;
    const model = api.getFilterModel() || {};
    if (model[field] == null) return;
    const { [field]: _removed, ...rest } = model;
    api.setFilterModel(rest);
  }, [contextMenu?.field, agGridRef]);

  const handleContextClearAllFilters = useCallback(() => {
    handleCloseContextMenu();
    const api = agGridRef.current?.api;
    api?.setFilterModel(null);
  }, [agGridRef]);

  // 우클릭 메뉴 - 컬럼 그룹핑(조회전용 그리드): 우클릭한 컬럼(셀/헤더)의 값별로 묶어서 보기 / 해제
  const groupMenuCol = contextMenu?.groupCol && !GROUP_EXCLUDE_FIELDS.includes(contextMenu.groupCol) && findColDef(contextMenu.groupCol) ? contextMenu.groupCol : undefined;
  // 우클릭한 컬럼을 다음 그룹 단계로 추가(이미 그룹 기준이면 메뉴 자체를 안 보여줌)
  const handleContextGroupBy = useCallback(() => {
    const col = groupMenuCol;
    handleCloseContextMenu();
    if (!col) return;
    setGroupFields(prev => {
      const next = prev.includes(col) ? prev : [...prev, col];
      // 그룹핑하면 기본으로 접힌 상태로 보여줌 - 항상 0단계(최상위) 그룹 기준으로 계산함.
      // (0단계가 접히면 build()가 하위 단계를 만들지 않으므로, 몇 단계를 추가하든 이 하나로 전부 접힘)
      const topField = next[0];
      const colDef = findColDef(topField);
      const rows = ((agGridRows as any[]) || []).filter(r => !isSynthRow(r));
      if (colDef && rows.length > 0) {
        const keys = new Set<string>();
        rows.forEach(row => {
          const raw = row?.[topField];
          let v: any = raw;
          if (typeof colDef.valueFormatter === 'function') {
            try { v = colDef.valueFormatter({ value: raw, data: row, colDef, node: null, api: agGridRef.current?.api, column: null, context: undefined }); } catch { v = raw; }
          }
          keys.add(v == null || String(v).trim() === '' ? GROUP_EMPTY_LABEL : String(v));
        });
        setCollapsedGroups(keys);
      } else {
        setCollapsedGroups(new Set());
      }
      return next;
    });
  }, [groupMenuCol, findColDef, agGridRows, agGridRef]);
  const handleContextUngroupLast = useCallback(() => {
    handleCloseContextMenu();
    setCollapsedGroups(new Set());
    setGroupFields(prev => prev.slice(0, -1));
  }, []);
  const handleContextClearGroup = useCallback(() => {
    handleCloseContextMenu();
    setGroupFields([]);
    setCollapsedGroups(new Set());
  }, []);

  // 우클릭 메뉴 항목 공통 스타일 - 간격이 넓다는 피드백으로 상하 여백 축소(py, minHeight)
  const contextMenuItemSx = {
    display: 'flex',
    alignItems: 'center',
    gap: 1.25,
    borderRadius: 1.5,
    mx: 0.25,
    px: 1.5,
    py: 0.4,
    minHeight: 'auto',
    fontSize: 13,
    '&:hover': { backgroundColor: 'action.hover' },
  } as const;

  /////////////////////////////////////////////////////////////////////////////////////////////////////////
  // 사용자별 그리드 컬럼 레이아웃(순서/너비) 복원 - ZA_USER_GRID (callPgmId+gridId 둘 다 있을 때만 동작)
  //  - 저장은 자동으로 하지 않음(사용자 판단) - saveGridLayout()/resetGridLayout()을 화면에서 버튼으로 명시 호출
  /////////////////////////////////////////////////////////////////////////////////////////////////////////
  // 페이지 크기/페이징 보임여부/페이지크기 선택목록 - 컬럼이 아니라 OBJ_ID='pageSize'/'pagination'/'pageSizeSelector'
  // 행에 담겨 오므로, 컬럼 상태(state)로 들어가기 전에 따로 빼서 그리드 옵션으로 적용함(2026-10-07).
  const PAGING_OBJ_IDS = ['pageSize', 'pagination', 'pageSizeSelector'];

  const restoreGridLayout = useCallback(async (api: any) => {
    if (!callPgmId || !gridId) return;
    const userId = getUserInfo()?.userId || '';
    if (!userId) return;
    try {
      const res = await axios.post(`${API_BASE_URL}/com/apiUserGridLayout`, { programId: callPgmId, gridId });
      const rows: { OBJ_ID: string; X_POS: number; WIDTH: number; HIDE?: 'Y' | 'N' | null; PINNED?: 'left' | 'right' | null; PAGESIZE?: number | null; PAGINATION?: string | null; PAGESIZESELECTOR?: string | null }[] = res?.data?.rsResults || [];
      if (!Array.isArray(rows) || rows.length === 0) return;
      const pagingRows = rows.filter(r => PAGING_OBJ_IDS.includes(r.OBJ_ID));
      const state = rows
        .filter(r => !PAGING_OBJ_IDS.includes(r.OBJ_ID))
        .map(r => ({
          colId: r.OBJ_ID,
          width: r.WIDTH != null ? Number(r.WIDTH) : undefined,
          hide: r.HIDE === 'Y',
          pinned: (r.PINNED === 'left' || r.PINNED === 'right') ? r.PINNED : null,
        }));
      // await 사이(서버 응답 대기 중)에 탭을 닫아 그리드가 destroy됐을 수 있음 - 아래와 같은 이유로 방어.
      if (api?.isDestroyed?.()) return;
      if (state.length > 0) {
        api?.applyColumnState?.({ state, applyOrder: true });
        savedLayoutByApi.set(api, state.map(({ hide, ...rest }) => rest)); // 숨김은 columnDefs 변경에도 유지되므로 제외(컬럼선택기 변경분을 덮지 않음)
      }
      // 페이징 옵션 - 셋 다 updateGridOptions() 한 번에 같이 적용함. paginationPageSize만 따로(paginationSetPageSize)
      // 호출하면 앞의 pagination/paginationPageSizeSelector 변경이 비동기로 페이지네이션을 다시 초기화하면서
      // 그 직후의 별도 호출을 덮어써버림(2026-10-07 확인) - 한 번의 updateGridOptions로 묶어서 그 경쟁을 없앰.
      const paginationRow = pagingRows.find(r => r.OBJ_ID === 'pagination');
      const selectorRow = pagingRows.find(r => r.OBJ_ID === 'pageSizeSelector');
      const pageSizeRow = pagingRows.find(r => r.OBJ_ID === 'pageSize');
      const gridOptionPatch: Record<string, any> = {};
      if (paginationRow?.PAGINATION != null) gridOptionPatch.pagination = paginationRow.PAGINATION === 'true';
      if (selectorRow?.PAGESIZESELECTOR != null) {
        try { gridOptionPatch.paginationPageSizeSelector = JSON.parse(selectorRow.PAGESIZESELECTOR); } catch { /* 저장된 값이 깨졌으면 무시 */ }
      }
      if (pageSizeRow?.PAGESIZE != null) gridOptionPatch.paginationPageSize = Number(pageSizeRow.PAGESIZE);
      if (Object.keys(gridOptionPatch).length > 0) api?.updateGridOptions?.(gridOptionPatch);
    } catch (_) {
      // 조회 실패 시 기본 컬럼 정의로 그대로 표시(무시)
    }
  }, [callPgmId, gridId]);

  /////////////////////////////////////////////////////////////////////////////////////////////////////////
  // 범위 선택 복사 - 셀을 드래그하면 사각형 범위(파란 배경)가 표시되고 Ctrl+C로 탭구분(TSV) 복사(엑셀에 그대로 붙여넣기).
  //  - ag-Grid 범위선택(cellSelection)은 Enterprise 전용이라 직접 구현함. 화면표시값(valueFormatter 적용)을 복사함.
  //  - 선택된 행/범위가 없어도 포커스된 셀 1개는 그 값을 복사함(2026-09-27). 단, 셀 안 텍스트를 실제로
  //    드래그선택한 상태면 그 텍스트 그대로 복사되도록 브라우저 기본 동작을 건드리지 않음. 셀 편집 중에도 가로채지 않음.
  //  - 범위 정보는 ref로만 관리하고(리렌더 방지) 바뀔 때 refreshCells로 셀 클래스(ag-cell-range)만 다시 계산함.
  /////////////////////////////////////////////////////////////////////////////////////////////////////////
  const wrapperRef = useRef<HTMLDivElement>(null);
  // 2026-09-24: 행이 2개 이상 선택되면(멀티선택) 선택된 행을 연두색으로 표시 - 래퍼에 aggrid-multi-selected 클래스를 붙여 CSS로 처리
  const [multiSelected, setMultiSelected] = useState(false);
  //  - 2026-09-24: No(순번) 컬럼 클릭 = 행 전체 선택(rowMode) - 클릭하면 그 행 전체가 범위로 잡히고, No 위에서 아래로 드래그하면 여러 행,
  //    Shift+클릭이면 마지막 클릭 행부터 범위 확장. 복사할 때는 No/CUD 컬럼을 뺌. (Ctrl+클릭은 ag-Grid 기본 다중 행 선택에 맡김)
  const rangeRef = useRef<{ rowMin: number; rowMax: number; colIds: Set<string>; rowMode?: boolean } | null>(null);
  const dragRef = useRef<{ row: number; colId: string; endRow: number; endColId: string; moved: boolean; rowMode?: boolean } | null>(null);
  // 2026-09-26: Ctrl+클릭으로 셀을 하나씩 누적하는 셀 다중선택(떨어진 셀도 가능) - key = 'rowIndex|colId'. 드래그 사각 범위(rangeRef)와는 별개이며 서로 배타적으로 사용.
  const cellSetRef = useRef<Map<string, { row: number; colId: string }>>(new Map());
  // 2026-09-26: 숫자컬럼만 드래그/누적 선택했을 때 마우스 옆에 뜨는 합계/개수 툴팁(범위가 풀리면 사라짐)
  const [rangeTip, setRangeTip] = useState<{ x: number; y: number; sum: number | null; sumPartial: boolean; rows: number; multiCol: boolean; count: number } | null>(null);
  const updateTipRef = useRef<((x: number, y: number) => void) | null>(null);
  const lastTipRef = useRef(0);   // 드래그 중 갱신 간격 제한용(ms)
  const anchorRowRef = useRef<number | null>(null);   // 마지막으로 마우스를 누른 행(Shift+클릭 범위 확장의 시작점)

  const applyRange = useCallback((api: any, d: { row: number; colId: string; endRow: number; endColId: string; rowMode?: boolean }) => {
    const cols: any[] = api.getAllDisplayedColumns?.() || [];
    if (d.rowMode) {   // 행 전체 선택 - 모든 표시 컬럼
      const rowMin = Math.min(d.row, d.endRow);
      const rowMax = Math.max(d.row, d.endRow);
      rangeRef.current = { rowMin, rowMax, colIds: new Set(cols.map(c => c.getColId())), rowMode: true };
      // 2026-09-24: 범위 하이라이트(파랑) 대신 실제 행 선택으로 반영 -> 2행 이상이면 멀티선택 연두색, 행삭제/행취소도 이 행들에 적용됨
      const nodes: any[] = [];
      for (let i = rowMin; i <= rowMax; i++) { const n = api.getDisplayedRowAtIndex?.(i); if (n && !isSynthRow(n.data)) nodes.push(n); }
      api.deselectAll?.();
      nodes.forEach(n => n.setSelected(true));
      api.refreshCells?.();
      return;
    }
    const i1 = cols.findIndex(c => c.getColId() === d.colId);
    const i2 = cols.findIndex(c => c.getColId() === d.endColId);
    if (i1 < 0 || i2 < 0) return;
    const ids = cols.slice(Math.min(i1, i2), Math.max(i1, i2) + 1).map(c => c.getColId());
    rangeRef.current = { rowMin: Math.min(d.row, d.endRow), rowMax: Math.max(d.row, d.endRow), colIds: new Set(ids) };
    api.refreshCells?.();
  }, []);
  const clearRange = useCallback((api?: any) => {
    setRangeTip(null);
    if (!rangeRef.current && cellSetRef.current.size === 0) return;
    rangeRef.current = null;
    cellSetRef.current = new Map();
    (api || agGridRef.current?.api)?.refreshCells?.();
  }, [agGridRef]);

  // 드래그 종료는 그리드 밖에서 마우스를 놓는 경우도 있으므로 document 레벨에서 처리
  useEffect(() => {
    const onUp = (ev: MouseEvent) => {
      const d = dragRef.current;
      if (d) {
        dragRef.current = null;
        wrapperRef.current?.classList.remove('aggrid-range-dragging');
        if (d.moved) updateTipRef.current?.(ev.clientX, ev.clientY);
      }
    };
    document.addEventListener('mouseup', onUp);
    return () => document.removeEventListener('mouseup', onUp);
  }, []);
  // 조회/그룹핑 변경으로 행이 바뀌면 범위 해제
  useEffect(() => { clearRange(); }, [agGridRows, groupFields, clearRange]);

  // 복사할 텍스트 생성 - 우선순위: 1) 파란 범위(2셀 이상) 2) 선택된 행 2개 이상(표시 컬럼 전체, No/CUD 제외)
  //   3) (우클릭 메뉴에서만) 우클릭한 셀 1개. 해당 없으면 null. 화면 표시값(valueFormatter 적용) 기준 탭구분 텍스트.
  // 2026-09-27: allowFocusedFallback=false는 문서 레벨 폴백(마스터/디테일용, 실제 DOM 포커스가 이 그리드에
  // 있는지 보장 안 됨) 전용 - "포커스된 셀"만으로 복사 대상을 잡으면, 그리드를 한 번이라도 클릭했던 화면에서
  // 포커스가 body에 남아있는 채로(예: 다른 곳의 일반 텍스트를 마우스로 선택) Ctrl+C를 누르면 그 오래된
  // 그리드 셀 값이 복사되면서 페이지 텍스트 복사가 막혀버리는 문제가 있었음. 래퍼 자신의 실제 키입력
  // (onKeyDown, DOM 포커스가 진짜 이 그리드에 있음이 보장됨)에서만 true로 씀.
  const getCopyText = useCallback((api: any, withHeader: boolean, source?: { rowIndex?: number; field?: string }, allowFocusedFallback: boolean = true): string | null => {
    if (cellSetRef.current.size) {   // Ctrl+클릭 셀 다중선택 - 선택된 셀이 걸친 행/컬럼만 표로 만들고 선택 안 된 칸은 빈칸
      const dispIds: string[] = (api.getAllDisplayedColumns?.() || []).map((c: any) => c.getColId());
      const cells = Array.from(cellSetRef.current.values());
      const ids = dispIds.filter((id: string) => cells.some(c => c.colId === id));
      const rowsIdx = Array.from(new Set(cells.map(c => c.row))).sort((a, b) => a - b);
      const cleanCell = (v: any) => (v == null ? '' : String(v).replace(/[\t\r\n]+/g, ' '));
      const out: string[] = [];
      if (withHeader) out.push(ids.map((id: string) => cleanCell(api.getColumn?.(id)?.getColDef?.()?.headerName ?? id)).join('\t'));
      rowsIdx.forEach(r => {
        const node = api.getDisplayedRowAtIndex?.(r);
        if (!node || isSynthRow(node.data)) return;
        out.push(ids.map((id: string) => {
          if (!cellSetRef.current.has(r + '|' + id)) return '';
          let v: any;
          try { v = api.getCellValue({ rowNode: node, colKey: id, useFormatter: true }); } catch { v = node.data?.[id]; }
          return cleanCell(v);
        }).join('\t'));
      });
      return out.length ? out.join('\n') : null;
    }    const displayed: any[] = api.getAllDisplayedColumns?.() || [];
    const R = rangeRef.current;
    const hasRange = !!R && !(R.rowMin === R.rowMax && R.colIds.size <= 1);
    let nodes: any[] = [];
    let cols: any[] = [];
    if (hasRange && R) {
      cols = displayed.filter(c => R.colIds.has(c.getColId()) && !(R.rowMode && ['no', 'cud'].includes(c.getColId())));
      if (R.rowMode) {
        nodes = (api.getSelectedNodes?.() || []).filter((n: any) => n && !isSynthRow(n.data)).sort((a: any, b: any) => (a.rowIndex ?? 0) - (b.rowIndex ?? 0));
      }
      if (!nodes.length) for (let i = R.rowMin; i <= R.rowMax; i++) { const n = api.getDisplayedRowAtIndex?.(i); if (n) nodes.push(n); }
    } else {
      const selected: any[] = (api.getSelectedNodes?.() || []).filter((n: any) => n && !isSynthRow(n.data)).sort((a: any, b: any) => (a.rowIndex ?? 0) - (b.rowIndex ?? 0));
      if (selected.length >= 2) {
        nodes = selected;
        cols = displayed.filter(c => !['no', 'cud'].includes(c.getColId()));
      } else if (source?.field && source.rowIndex != null) {
        const n = api.getDisplayedRowAtIndex?.(source.rowIndex);
        const c = displayed.find(col => col.getColId() === source.field || col.getColDef?.()?.field === source.field);
        if (n && c) { nodes = [n]; cols = [c]; }
      } else if (selected.length === 1 && allowFocusedFallback) {
        // 2026-09-27: 행이 딱 1개만 선택된 경우. 일반 셀을 클릭해도 그 행이 부수적으로 선택되기 때문에,
        // "No컬럼으로 명시적으로 행을 선택"한 게 아니라 그냥 셀 하나를 클릭한 거면 그 셀 하나만 복사해야
        // 함(행 전체를 복사하면 사용자가 원한 것보다 훨씬 많은 값이 복사됨). 포커스된 컬럼이 no면(No컬럼
        // 클릭/Shift로 잡은 명시적 단일 행선택) 행 전체를, 아니면(일반 셀 클릭) 그 셀 하나만 복사함.
        // allowFocusedFallback=false(그리드 밖 body로 포커스가 완전히 빠진 문서 폴백)면 이 통째로 건너뜀 -
        // 단일 행선택은 셀 클릭만 해도 생기는 흔한 부수효과라, 포커스가 진짜 이 그리드에 없으면 신뢰 못함.
        const focused = api.getFocusedCell?.();
        const focusedColId = focused?.column?.getColId?.();
        if (focusedColId && focusedColId !== 'no' && !['no', 'cud'].includes(focusedColId)) {
          const n = api.getDisplayedRowAtIndex?.(focused.rowIndex);
          const c = displayed.find((col: any) => col.getColId() === focusedColId);
          if (n && c) { nodes = [n]; cols = [c]; }
        } else {
          nodes = selected;
          cols = displayed.filter(c => !['no', 'cud'].includes(c.getColId()));
        }
      } else if (allowFocusedFallback) {
        // 선택된 행도 없고 우클릭 메뉴도 아닌 경우(Ctrl+C) - 현재 포커스된 셀 1개를 복사 대상으로 함.
        const focused = api.getFocusedCell?.();
        if (focused) {
          const n = api.getDisplayedRowAtIndex?.(focused.rowIndex);
          const c = displayed.find((col: any) => col.getColId() === focused.column?.getColId?.());
          if (n && c && !['no', 'cud'].includes(c.getColId())) { nodes = [n]; cols = [c]; }
        }
      }
    }
    nodes = nodes.filter(n => !n.data?.__group);
    if (!nodes.length || !cols.length) return null;
    const clean = (v: any) => (v == null ? '' : String(v).replace(/[\t\r\n]+/g, ' '));
    const lines: string[] = [];
    if (withHeader) lines.push(cols.map(c => clean(c.getColDef?.()?.headerName ?? c.getColId())).join('\t'));
    nodes.forEach(node => {
      const isSub = !!node.data?.__subtotal;
      lines.push(cols.map((c: any) => {
        const colId = c.getColId();
        const useFormatter = !isSub || (node.data.__sumFields || []).includes(colId);   // 소계행의 라벨은 원문 그대로
        let v: any;
        try { v = api.getCellValue({ rowNode: node, colKey: colId, useFormatter }); } catch { v = node.data?.[colId]; }
        return clean(v);
      }).join('\t'));
    });
    return lines.join('\n');
  }, []);

  const writeClipboard = useCallback((text: string) => {
    const fallbackCopy = () => {
      const ta = document.createElement('textarea');
      ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
      document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); } catch { /* 복사 불가 환경 - 무시 */ }
      document.body.removeChild(ta);
    };
    if (navigator.clipboard?.writeText) navigator.clipboard.writeText(text).catch(fallbackCopy);   // http(비보안) 환경 등에서 실패하면 대체 경로
    else fallbackCopy();
  }, []);

  // 셀 하나 "안"에서 텍스트를 실제로 드래그선택(더블클릭 등)한 경우에만 true - Shift+클릭으로 행 범위를
  // 잡을 때도 브라우저가 부수적으로 여러 셀에 걸친 텍스트선택을 만들어버리는 경우가 있어서, anchor/focus가
  // 같은 .ag-cell 안에 있을 때만(=진짜 한 셀 내부 선택) 브라우저 기본 복사에 맡기도록 좁힘(2026-09-27).
  const isSingleCellTextSelection = (): boolean => {
    const sel = window.getSelection?.();
    if (!sel || sel.toString().length === 0) return false;
    const cellOf = (node: Node | null): Element | null => {
      if (!node) return null;
      const el = node.nodeType === Node.TEXT_NODE ? node.parentElement : (node as Element);
      return el?.closest?.('.ag-cell') ?? null;
    };
    const a = cellOf(sel.anchorNode);
    const b = cellOf(sel.focusNode);
    return !!a && a === b;
  };

  // 브라우저에 "실제로 선택된 텍스트"가 있고, 그 선택이 그리드(.ag-root-wrapper, 이 그리드든 다른 그리드든) 밖에
  // 걸쳐 있는지 - true면 화면의 라벨/텍스트 등을 마우스로 긁어 선택한 것이므로 문서 레벨 폴백이 가로채면 안 됨
  // (활성 그리드 데이터로 덮어써지던 버그, 2026-09-30). 선택 자체가 없을 때(sel.toString()==='')는 여기 해당 없음
  // - 그 경우는 기존처럼 "마지막으로 클릭한 활성 그리드"에 맡김(마스터/디테일 포커스 이동 대응 유지).
  const hasSelectionOutsideAnyGrid = (): boolean => {
    const sel = window.getSelection?.();
    if (!sel || sel.toString().length === 0) return false;
    const inGrid = (node: Node | null): boolean => {
      if (!node) return false;
      const el = node.nodeType === Node.TEXT_NODE ? node.parentElement : (node as Element);
      return !!el?.closest?.('.ag-root-wrapper');
    };
    return !(inGrid(sel.anchorNode) && inGrid(sel.focusNode));
  };
  // 셀 편집기가 아닌 입력창(컬럼 필터 팝업/플로팅필터/페이지 크기 입력 등)인지 - 이런 곳의 Ctrl+C/Ctrl+V는 그리드가 가로채지 않고
  // 브라우저 기본 복사/붙여넣기에 맡김(2026-10-18: 필터 입력창에 Ctrl+V가 안 되던 버그 - 그리드 붙여넣기가 preventDefault 했음)
  const isNonCellInput = (t: EventTarget | null): boolean => {
    const el = t as HTMLElement | null;
    if (!el || !el.tagName) return false;
    const isInput = el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable;
    return isInput && !el.closest?.('.ag-cell, .ag-popup-editor');   // 셀 편집기(셀 안/팝업 편집기)는 기존 그리드 처리 유지
  };

  // Ctrl+C = 헤더 없이 복사(범위/선택행/포커스셀 중 하나라도 있으면 그걸 복사), Esc = 범위 해제.
  //  - viaDocFallback=true(문서 레벨 폴백에서 호출됨 - 실제 DOM 포커스가 이 그리드에 있다는 보장이 없음)면
  //    "포커스된 셀만으로" 판단하는 예외는 빼고, 범위/선택행처럼 확실한 상태만 가로챔.
  const handleRangeKeyDown = useCallback((e: React.KeyboardEvent, viaDocFallback?: boolean) => {
    const api: any = agGridRef.current?.api;
    if (!api) return;
    if (e.key === 'Escape') { clearRange(api); return; }
    if (!((e.ctrlKey || e.metaKey) && (e.key === 'c' || e.key === 'C'))) return;
    if (isNonCellInput(e.target)) return;   // 필터 입력창 등 - 그 입력창 글자 복사(브라우저 기본)
    if ((api.getEditingCells?.() || []).length) return;
    if (isSingleCellTextSelection()) return;
    const text = getCopyText(api, false, undefined, !viaDocFallback);
    if (text == null) return;
    e.preventDefault();
    writeClipboard(text);
  }, [agGridRef, clearRange, getCopyText, writeClipboard]);

  // 선택범위(드래그 사각/Ctrl+클릭 다중선택) 크기 - 실제 2칸 이상 선택된 상태일 때만 {rows,cols} 반환,
  // 범위선택이 없으면(단일 셀만 포커스) null - 붙여넣기 시 "범위를 봐야 하는지" 판단용(2026-10-01).
  const getActiveRangeSize = useCallback((): { rows: number; cols: number } | null => {
    if (cellSetRef.current.size >= 2) {
      const cells = Array.from(cellSetRef.current.values());
      return { rows: new Set(cells.map(c => c.row)).size, cols: new Set(cells.map(c => c.colId)).size };
    }
    const R = rangeRef.current;
    if (R && !(R.rowMin === R.rowMax && R.colIds.size <= 1)) {
      return { rows: R.rowMax - R.rowMin + 1, cols: R.colIds.size };
    }
    return null;
  }, []);

  // 클립보드 텍스트의 행×열 크기(탭/개행 기준) - 선택범위와 크기 비교용(2026-10-01).
  const getClipboardBlockSize = (text: string): { rows: number; cols: number } => {
    const lines = stripOneTrailingNewline(text).split('\n');
    return { rows: lines.length, cols: lines.reduce((max, line) => Math.max(max, line.split('\t').length), 1) };
  };

  // 클립보드 텍스트(탭구분)를 포커스된 셀부터 채워넣기(Ctrl+V) - 엑셀에서 복사한 여러 행/열을 그대로 붙여넣기.
  //  - 2026-09-27: No/CUD 컬럼은 대상에서 뺌(복사 때와 동일). 대상 행이 모자라면(붙여넣을 데이터가 더 많으면)
  //    남는 건 그냥 버림 - 새 행을 자동으로 추가하지 않음(행추가로 필요한 만큼 먼저 늘려두고 붙여넣는 방식).
  //    편집 불가(pk/편집불가) 컬럼은 그 칸만 건너뜀. 값 반영은 node.setDataValue로 하므로 colDef의
  //    valueSetter(체크박스 Y/N, 날짜 압축형 등)가 그대로 적용되고, onCellValueChanged(화면별 검증 +
  //    setUpdateRow로 cud='U' 표기)도 수동편집과 동일하게 자동으로 탐.
  //  - clip(2026-10-01): 선택범위보다 큰 블록을 붙여넣을 때 "범위 크기로 자르기"를 선택한 경우에만 넘어옴
  //    (resolvePasteClip이 사용자 확인을 거쳐 결정) - 지정되면 그 행/열 수를 넘는 부분은 버림.
  const pasteTextToGrid = useCallback((api: any, text: string, clip?: { rows: number; cols: number }) => {
    if (isQueryOnlyGridApi(api)) return;
    const focused = api.getFocusedCell?.();
    if (!focused) return;
    const displayed: any[] = api.getAllDisplayedColumns?.() || [];
    const pasteable = displayed.filter((c: any) => !['no', 'cud'].includes(c.getColId()));
    const anchorColId = focused.column?.getColId?.();
    const startColIdx = pasteable.findIndex((c: any) => c.getColId() === anchorColId);
    if (startColIdx < 0) return;   // No/CUD 컬럼에 포커스된 상태면 붙여넣을 대상이 없음

    // 엑셀 복사 시 끝에 개행이 하나 더 붙어 나오는 관례만 제거함(딱 1개만) - while로 끝 빈 줄을 계속 지우면
    // "빈 셀 하나만 복사"한 경우(클립보드 내용이 그냥 "")까지 통째로 지워져서 아무것도 안 붙여지는 버그가
    // 있었음(2026-09-27). 순수 빈 값도 "그 칸을 지우기"로 정상 반영되어야 함.
    let lines = stripOneTrailingNewline(text).split('\n');
    if (clip) lines = lines.slice(0, clip.rows);

    lines.forEach((line, i) => {
      const node = api.getDisplayedRowAtIndex?.(focused.rowIndex + i);
      if (!node || isSynthRow(node.data)) return;   // 행이 모자라거나 소계행 등이면 그 줄부터는 건너뜀
      let cells = line.split('\t');
      if (clip) cells = cells.slice(0, clip.cols);
      cells.forEach((raw, j) => {
        const col = pasteable[startColIdx + j];
        if (!col) return;   // 그리드 오른쪽 끝을 넘어가는 열은 버림
        const colDef = col.getColDef?.() || {};
        const editable = typeof colDef.editable === 'function' ? !!colDef.editable({ data: node.data }) : !!colDef.editable;
        if (!editable) return;
        let value: any = raw;
        // 콤보(comboColDef) 컬럼 - 다른 컬럼을 복사해서 붙여넣으면 콤보에 없는 값(자유 텍스트)이 그대로
        // 셀에 들어가 버림. 특히 DB 컬럼이 NVARCHAR(1)처럼 짧게 잡힌 콤보(타입 등)에 긴 값이 붙여넣어지면
        // 저장 시 TVP 메타데이터 길이 오류(8037)로 터짐(Z1001M1 그리드2 '타입' 컬럼에서 실제 발생, 2026-10-02).
        // 그리드 복사는 useFormatter:true라 콤보 셀은 코드가 아니라 명칭이 복사되므로(예: 'S'->'시스템'),
        // 명칭이면 코드로 되돌리고, 코드도 명칭도 아니면(콤보 옵션에 없는 값) 그 칸만 건너뜀(나머지는 계속 진행).
        if (colDef.cellEditor === 'agSelectCellEditor') {
          const allowed: any[] = colDef.cellEditorParams?.values || [];
          const valueByLabel: Record<string, any> = colDef.cellEditorParams?.valueByLabel || {};
          if (allowed.some(v => String(v) === raw)) {
            value = raw;
          } else if (Object.prototype.hasOwnProperty.call(valueByLabel, raw)) {
            value = valueByLabel[raw];
          } else {
            return;
          }
        }
        const isNumericCol = colDef.type === 'numericColumn' || (Array.isArray(colDef.type) && colDef.type.includes('numericColumn'));
        if (isNumericCol && !colDef.valueSetter) {
          const cleaned = raw.replace(/,/g, '').trim();   // 엑셀 숫자 포맷(천단위 콤마 등) 제거 후 숫자로 변환
          value = cleaned === '' ? null : (isNaN(Number(cleaned)) ? raw : Number(cleaned));
        }
        node.setDataValue(col.getColId(), value);
      });
    });
  }, []);

  // 클립보드 내용이 탭/여러줄을 포함한 "블록"인지 판별(끝에 붙는 개행 하나는 무시 - 엑셀에서 셀 1개만
  // 복사해도 끝에 개행이 붙어서 나옴). 블록이면 편집 중이던 셀의 편집을 취소하고 블록 붙여넣기를 함 -
  // singleClickEdit 때문에 붙여넣기 시작점 셀을 클릭만 해도 편집모드로 들어가버려서, 이거 없이는 매번
  // Escape로 편집을 빠져나온 뒤에야 블록 붙여넣기가 되는 불편함이 있었음(2026-09-27).
  const isBlockClipboardText = (text: string): boolean => /[\t\n]/.test(stripOneTrailingNewline(text));

  // 붙여넣을 블록이 선택범위보다 큰 경우 확인(2026-10-01) - 범위선택이 없으면(단일 셀) 묻지 않고 그대로
  // 진행(undefined = 자르지 않음, 기존 동작). 범위보다 같거나 작으면도 묻지 않음. 범위보다 크면 확인모달:
  // 예 = 범위를 넘어서 클립보드 전체를 붙여넣음(undefined 반환), 아니오 = 선택범위 크기로 잘라서 붙여넣음.
  const resolvePasteClip = useCallback(async (text: string): Promise<{ rows: number; cols: number } | undefined> => {
    const rangeSize = getActiveRangeSize();
    if (!rangeSize) return undefined;
    const block = getClipboardBlockSize(text);
    if (block.rows <= rangeSize.rows && block.cols <= rangeSize.cols) return undefined;
    const confirmed = await confirmMsgBox({
      title: ddKey('lblConfirm') || '확인',
      msgText: ddKey('msgPasteExceedsRange') || '붙여넣을 내용이 선택한 범위보다 큽니다. 범위를 넘어서 전체를 붙여넣을까요?',
      showBtn: 'YesNo',
    });
    return confirmed ? undefined : rangeSize;
  }, [getActiveRangeSize, confirmMsgBox, ddKey]);
  const resolvePasteClipRef = useRef(resolvePasteClip);
  resolvePasteClipRef.current = resolvePasteClip;

  const handlePasteEvent = useCallback(async (e: React.ClipboardEvent) => {
    const api: any = agGridRef.current?.api;
    if (!api) return;
    if (isNonCellInput(e.target)) return;   // 필터 입력창 등 - 그 입력창에 브라우저 기본 붙여넣기
    const text = e.clipboardData?.getData('text/plain');
    // 2026-09-27: !text(falsy) 체크였는데, "빈 셀 하나만 복사"하면 클립보드가 그냥 ""(빈 문자열)이라
    // 이 체크에 걸려 아무것도 안 붙여지는 버그가 있었음. 진짜 클립보드에서 못 읽은 경우(null/undefined)만 무시.
    if (text == null) return;
    const editing = (api.getEditingCells?.() || []).length > 0;
    if (editing && !isBlockClipboardText(text)) return;   // 편집 중 + 단일값이면 그 입력창에 브라우저 기본 붙여넣기에 맡김
    e.preventDefault();
    if (editing) api.stopEditing(true);   // 블록 붙여넣기면 시작점 셀의 편집을 취소(원래값 복원)하고 진행
    const clip = await resolvePasteClip(text);
    pasteTextToGrid(api, text, clip);
  }, [agGridRef, pasteTextToGrid, resolvePasteClip]);

  // 마스터/디테일 화면에서는 행을 클릭하면 하위 그리드 조회 후 포커스가 다른 그리드로 넘어가서(selectFirstRow), 클릭한 그리드의
  // 래퍼에는 Ctrl+C 키 이벤트가 오지 않음 -> "파란 테두리(마지막으로 클릭한) 활성 그리드"가 문서 레벨에서도 Ctrl+C를 받게 함.
  //  - 포커스가 입력창/편집기 등에 있으면 건드리지 않고(body 또는 그리드 내부에 있을 때만), 다른 곳에서 이미 처리했으면(defaultPrevented) 건너뜀.
  const keyHandlerRef = useRef(handleRangeKeyDown);
  keyHandlerRef.current = handleRangeKeyDown;
  const activeForCopyRef = useRef(false);
  activeForCopyRef.current = isActiveGrid;
  useEffect(() => {
    const onDocKeyDown = (ev: KeyboardEvent) => {
      if (ev.defaultPrevented || !activeForCopyRef.current) return;
      if (!((ev.ctrlKey || ev.metaKey) && (ev.key === 'c' || ev.key === 'C'))) return;
      if (hasSelectionOutsideAnyGrid()) return;   // 그리드 밖 텍스트(라벨 등)를 선택해 복사하는 중 - 브라우저 기본 복사에 맡김
      const t = ev.target as HTMLElement | null;
      if (t && t !== document.body && !t.closest?.('.ag-root-wrapper')) return;
      if (wrapperRef.current?.contains(t)) return;   // 이 그리드 안이면 래퍼의 onKeyDown이 이미 처리함
      if (isNonCellInput(t)) return;   // 다른 그리드의 필터 입력창 등 - 브라우저 기본 복사
      // 2026-09-27: t가 다른 그리드의 .ag-root-wrapper 안이면(마스터/디테일에서 selectFirstRow가
      // setFocusedCell로 실제 포커스를 그 그리드로 옮긴 경우) 정상적인 폴백 대상이라 그대로 허용하지만,
      // t가 body(그리드 생태계를 완전히 벗어남 - 예: 페이지의 다른 텍스트를 마우스로 선택한 경우)면
      // "선택된 행 1개/포커스된 셀만" 같은 약한 신호는 못 믿으므로 그 예외들은 건너뜀(viaDocFallback=true).
      const targetLeftGridEntirely = !t || t === document.body;
      keyHandlerRef.current(ev as any, targetLeftGridEntirely);
    };
    document.addEventListener('keydown', onDocKeyDown);
    return () => document.removeEventListener('keydown', onDocKeyDown);
  }, []);

  // Ctrl+C와 동일한 이유로, 붙여넣기(Ctrl+V)도 "마지막으로 클릭한 활성 그리드"가 문서 레벨에서 받게 함.
  const pasteHandlerRef = useRef(pasteTextToGrid);
  pasteHandlerRef.current = pasteTextToGrid;
  useEffect(() => {
    const onDocPaste = async (ev: ClipboardEvent) => {
      if (ev.defaultPrevented || !activeForCopyRef.current) return;
      const api: any = agGridRef.current?.api;
      if (!api) return;
      const t = ev.target as HTMLElement | null;
      if (t && t !== document.body && !t.closest?.('.ag-root-wrapper')) return;
      if (wrapperRef.current?.contains(t)) return;   // 이 그리드 안이면 래퍼의 onPaste가 이미 처리함
      if (isNonCellInput(t)) return;   // 다른 그리드의 필터 입력창 등 - 브라우저 기본 붙여넣기
      const text = ev.clipboardData?.getData('text/plain');
      if (text == null) return;   // 빈 셀 하나만 복사한 경우("")도 정상 붙여넣기 대상이라 falsy 체크 안 함
      const editing = (api.getEditingCells?.() || []).length > 0;
      if (editing && !isBlockClipboardText(text)) return;
      ev.preventDefault();
      if (editing) api.stopEditing(true);
      const clip = await resolvePasteClipRef.current(text);
      pasteHandlerRef.current(api, text, clip);
    };
    document.addEventListener('paste', onDocPaste);
    return () => document.removeEventListener('paste', onDocPaste);
  }, [agGridRef]);

  // 우클릭 메뉴 - 복사 / 복사(헤더포함)
  const canCopy = (() => {
    if (!contextMenu) return false;
    if (cellSetRef.current.size >= 1) return true;
    const R = rangeRef.current;
    if (R && !(R.rowMin === R.rowMax && R.colIds.size <= 1)) return true;
    if (((agGridRef.current?.api as any)?.getSelectedNodes?.() || []).filter((n: any) => n && !isSynthRow(n.data)).length >= 2) return true;
    return !!contextMenu.field && contextMenu.rowIndex != null;
  })();
  // 우클릭 메뉴 - 셀을 드래그해서 잡은 블록(파란 범위, 2셀 이상)의 집계: 선택한 컬럼이 전부 숫자컬럼이면 합계+개수, 그 외는 개수만.
  //   No 컬럼 클릭으로 잡은 행 전체 선택(rowMode)이나 행 다중선택은 대상이 아님. 개수 = 값이 비어있지 않은 셀 수(No/CUD 컬럼, 그룹/소계 행 제외).
  //   합계는 화면 서식이 아닌 원본값 기준.
  const computeBlockStats = useCallback(() => {
    const api: any = agGridRef.current?.api;
    if (!api) return null;
    const displayed: any[] = api.getAllDisplayedColumns?.() || [];
    const colById = (id: string) => displayed.find((c: any) => c.getColId() === id);
    const pairs: { row: number; colId: string }[] = [];
    if (cellSetRef.current.size >= 2) {   // Ctrl+클릭으로 누적한 셀들
      cellSetRef.current.forEach(v => { if (colById(v.colId)) pairs.push(v); });
    } else {                              // 드래그로 잡은 사각 범위
      const R = rangeRef.current;
      if (!R || R.rowMode || (R.rowMin === R.rowMax && R.colIds.size <= 1)) return null;
      const ids = displayed.map((c: any) => c.getColId()).filter((id: string) => R.colIds.has(id) && !['no', 'cud'].includes(id));
      for (let i = R.rowMin; i <= R.rowMax; i++) ids.forEach((id: string) => pairs.push({ row: i, colId: id }));
    }
    if (!pairs.length) return null;
    const cols = Array.from(new Set(pairs.map(p => p.colId))).map(colById).filter(Boolean);
    const isNum = (c: any) => { const ty = c.getColDef?.()?.type; return ty === 'numericColumn' || (Array.isArray(ty) && ty.includes('numericColumn')); };
    const numIds = new Set(cols.filter(isNum).map((c: any) => c.getColId()));   // 숫자컬럼만 합계 대상(숫자/비숫자 컬럼을 같이 잡으면 숫자컬럼 합계만 표시)
    const allNumeric = numIds.size === cols.length;
    let count = 0, sum = 0;
    const rowSet = new Set<number>();   // 행개수 - 블록이 걸친 (그룹/소계 아닌) 행 수
    for (const p of pairs) {
      const node = api.getDisplayedRowAtIndex?.(p.row);
      if (!node || isSynthRow(node.data)) continue;
      rowSet.add(p.row);
      let v: any;
      try { v = api.getCellValue({ rowNode: node, colKey: p.colId, useFormatter: false }); } catch { v = node.data?.[p.colId]; }
      if (v == null || String(v).trim() === '') continue;
      count++;
      if (numIds.has(p.colId)) { const n = Number(String(v).replace(/,/g, '')); if (!Number.isNaN(n)) sum += n; }
    }
    return { count, sum: numIds.size > 0 ? sum : (null as number | null), sumPartial: numIds.size > 0 && !allNumeric, rows: rowSet.size, multiCol: cols.length > 1 };
  }, [agGridRef]);
  const blockStats = contextMenu ? computeBlockStats() : null;
  // 툴팁 갱신 함수(mouseup/드래그/Ctrl+클릭에서 ref로 호출) - 숫자컬럼만 잡았을 때(합계가 부분합이 아닐 때)만 표시
  updateTipRef.current = (x: number, y: number) => {
    const s = computeBlockStats();
    if (s) { setRangeTip({ x, y, sum: s.sum, sumPartial: s.sumPartial, rows: s.rows, multiCol: s.multiCol, count: s.count }); return; }
    // No 컬럼으로 행 드래그/행 전체 선택/Ctrl+No 다중선택 - 선택된 행 수만 개수로 표시(합계/행개수 없음)
    const selCount = ((agGridRef.current?.api as any)?.getSelectedNodes?.() || []).filter((n: any) => n && !isSynthRow(n.data)).length;
    if (selCount >= 2) setRangeTip({ x, y, sum: null, sumPartial: false, rows: selCount, multiCol: false, count: selCount });
    else setRangeTip(null);
  };
  // 우클릭 메뉴 블록(구분선으로 나뉜 묶음)별 배경색 - 그리드 행 줄무늬(짝수 흰색 / 홀수 연한 회색 #eceff3 - 그리드 줄무늬(--ag-odd-row-background-color #f8fafc)보다 조금 진하게)처럼
  // 블록마다 번갈아 구분함. 조건부로 안 보이는 블록은 건너뛰고 실제로 보이는 블록만 세어서 번갈아 적용.
  // 2026-10-07: "그리드위치저장/초기화"+"그리드위치전체저장/취소" 2개 블록을 "그리드위치" 서브메뉴 1개로 합침(인덱스 1개 감소).
  const rmenuBlockVisible = [!!blockStats, canCopy, true, true, agGridType?.type === 'Q' && Boolean((groupMenuCol && !groupFields.includes(groupMenuCol)) || groupFields.length > 0), !!matchedRMenu];
  const blockSx = (k: number) => ({ ...contextMenuItemSx, backgroundColor: rmenuBlockVisible.slice(0, k).filter(Boolean).length % 2 === 0 ? '#ffffff' : '#eceff3' });
  const handleContextCopy = useCallback((withHeader: boolean) => {
    const cm = contextMenu;
    handleCloseContextMenu();
    const api: any = agGridRef.current?.api;
    if (!api) return;
    const text = getCopyText(api, withHeader, { rowIndex: cm?.rowIndex, field: cm?.field });
    if (text != null) writeClipboard(text);
  }, [contextMenu, agGridRef, getCopyText, writeClipboard]);

  // Ctrl+클릭(No/CUD 아닌 셀) = 그 셀만 선택에 추가/제거(셀 다중선택). ag-Grid의 기본 Ctrl+클릭(행 다중선택)이 일어나지 않도록 캡처 단계에서 막음.
  //   Ctrl+No 클릭은 막지 않음 = ag-Grid 기본 동작(클릭한 행만 행 단위로 다중선택). 기존 드래그 사각 범위가 있으면 셀 목록으로 풀어서 이어서 누적.
  const handleGridClickCapture = useCallback((e: React.MouseEvent) => {
    if (!(e.ctrlKey || e.metaKey) || e.button !== 0) return;
    const cellEl = (e.target as HTMLElement)?.closest?.('.ag-cell') as HTMLElement | null;
    if (!cellEl) return;
    const colId = cellEl.getAttribute('col-id');
    const rowEl = cellEl.closest('.ag-row') as HTMLElement | null;
    const rowIndex = rowEl ? Number(rowEl.getAttribute('row-index')) : NaN;
    if (!colId || Number.isNaN(rowIndex) || ['no', 'cud'].includes(colId)) return;
    const api: any = agGridRef.current?.api;
    if (!api || (api.getEditingCells?.() || []).length) return;
    const node = api.getDisplayedRowAtIndex?.(rowIndex);
    if (!node || isSynthRow(node.data)) return;
    e.stopPropagation();
    const next = new Map(cellSetRef.current);
    const R = rangeRef.current;
    if (!next.size && R && !R.rowMode) {   // 드래그로 잡아둔 사각 범위를 셀 목록으로 풀어서 합침
      const ids = (api.getAllDisplayedColumns?.() || []).map((c: any) => c.getColId()).filter((id: string) => R.colIds.has(id) && !['no', 'cud'].includes(id));
      for (let i = R.rowMin; i <= R.rowMax; i++) ids.forEach((id: string) => next.set(i + '|' + id, { row: i, colId: id }));
    }
    const key = rowIndex + '|' + colId;
    if (next.has(key)) next.delete(key); else next.set(key, { row: rowIndex, colId });
    rangeRef.current = null;
    cellSetRef.current = next;
    api.refreshCells?.();
    updateTipRef.current?.(e.clientX, e.clientY);
  }, [agGridRef]);

  const mergedEventHandlers = useMemo(() => {
    return {
      ...eventHandlers,
      onCellMouseDown: (e: any) => {
        (eventHandlers as any)?.onCellMouseDown?.(e);
        const me: MouseEvent | undefined = e?.event;
        if (!me || me.button !== 0) return;
        const api = e.api;
        if ((api.getEditingCells?.() || []).length) { dragRef.current = null; return; }   // 편집 중인 셀 안에서의 드래그는 텍스트 선택 - 범위선택 아님
        const colId = e.column?.getColId?.();
        if (colId == null || e.rowIndex == null) return;
        if (me.ctrlKey || me.metaKey) {   // Ctrl+클릭 : No = ag-Grid 기본 행 다중선택에 맡김 / 그 외 셀 = handleGridClickCapture가 셀 단위로 누적 - 여기서는 선택을 지우거나 드래그를 시작하지 않음
          dragRef.current = null;
          if (colId === 'no') clearRange(api);
          return;
        }
        const prevAnchor = anchorRowRef.current;
        anchorRowRef.current = e.rowIndex;
        if (colId === 'no' && !me.ctrlKey && !me.metaKey) {   // No 클릭 = 행 전체 선택(Shift면 마지막 클릭 행부터 확장)
          const from = me.shiftKey && prevAnchor != null ? prevAnchor : e.rowIndex;
          anchorRowRef.current = from;
          dragRef.current = { row: from, colId, endRow: e.rowIndex, endColId: colId, moved: false, rowMode: true };
          applyRange(api, dragRef.current);
          updateTipRef.current?.(me.clientX, me.clientY);
          return;
        }
        clearRange(api);
        dragRef.current = { row: e.rowIndex, colId, endRow: e.rowIndex, endColId: colId, moved: false };
      },
      onCellMouseOver: (e: any) => {
        const d = dragRef.current;
        if (!d || !((e?.event as MouseEvent)?.buttons & 1)) return;
        const colId = e.column?.getColId?.();
        if (colId == null || e.rowIndex == null) return;
        if (d.rowMode ? d.endRow === e.rowIndex : (d.endRow === e.rowIndex && d.endColId === colId)) return;
        d.endRow = e.rowIndex; d.endColId = colId;
        if (!d.moved) {   // 다른 셀로 넘어가는 순간 범위선택 시작 - 브라우저 텍스트 선택이 같이 잡히지 않게 함
          d.moved = true;
          wrapperRef.current?.classList.add('aggrid-range-dragging');
          window.getSelection?.()?.removeAllRanges();
        }
        applyRange(e.api, d);
        const nowMs = Date.now();
        const mev = e?.event as MouseEvent;
        if (mev && nowMs - lastTipRef.current > 80) { lastTipRef.current = nowMs; updateTipRef.current?.(mev.clientX, mev.clientY); }
      },
      onCellClicked: (e: any) => {
        const ce = e?.event as MouseEvent | undefined;
        if (ce && (ce.ctrlKey || ce.metaKey) && e?.column?.getColId?.() === 'no') {   // Ctrl+No 다중선택 - 행 선택이 반영된 뒤 개수 툴팁 갱신
          const cx = ce.clientX, cy = ce.clientY;
          setTimeout(() => updateTipRef.current?.(cx, cy), 0);
        }
        if (e?.data?.__subtotal) return;
        (eventHandlers as any)?.onCellClicked?.(e);
      },
      onGridReady: (params: any) => {
        // callPgmId/gridId 유무와 상관없이 항상 등록 - setInsertRow 등에서 Q타입 여부 판단에 사용.
        gridTypeByApi.set(params.api, agGridType?.type);
        originStoreByApi.set(params.api, originStoreRef.current);   // 행취소(setCancelRow)가 조회 당시 값을 찾는 저장소
        if (callPgmId && gridId) {
          restoreGridLayout(params.api);
          registerGrid(callPgmId, gridId, params.api); // 우클릭 "전체저장/전체취소"에서 이 그리드를 찾을 수 있도록 등록
        }
        eventHandlers?.onGridReady?.(params);
      },
      onCellContextMenu: handleCellContextMenu, // 셀 우클릭 -> [OOO 팝업 열기] 메뉴 항목용(필드/값 캡처)
      // 컬럼 그룹핑 중에는 그룹헤더행이 선택되지 않게 하고(selectFirstRow/키보드 이동 대비), 그룹행 이벤트는 화면에 전달하지 않음
      onSelectionChanged: (e: any) => {
        setMultiSelected((e.api.getSelectedNodes?.() || []).filter((n: any) => n && !isSynthRow(n.data)).length >= 2);
        if (groupActiveRef.current) {
          const groupNodes = (e.api.getSelectedNodes?.() || []).filter((n: any) => isSynthRow(n?.data));
          if (groupNodes.length) { groupNodes.forEach((n: any) => n.setSelected(false)); return; }
        }
        (eventHandlers as any)?.onSelectionChanged?.(e);
      },
      onRowSelected: (e: any) => { if (isSynthRow(e?.node?.data)) return; (eventHandlers as any)?.onRowSelected?.(e); },
      onRowClicked: (e: any) => { if (isSynthRow(e?.data)) return; (eventHandlers as any)?.onRowClicked?.(e); },
      // pk 컬럼(신규행에서만 editable) 또는 editable:false로 고정된 컬럼은, 기존행(수정, cud!=='C')에서
      // 더블클릭해도 화면의 onCellDoubleClicked(공통팝업 오픈 등)로 전달하지 않음 - pk는 기존행에서
      // 값을 바꾸면 안 되고, editable:false는 애초에 값을 바꿀 수 없는 순수 표시용 컬럼이기 때문. 신규행
      // (cud==='C')의 pk 컬럼은 그대로 통과시켜 공통팝업으로 채울 수 있게 함. 화면마다 이 cud 체크를
      // 반복하지 않도록 여기서 공통 처리.
      onCellDoubleClicked: (e: any) => {
        if (e?.data?.__subtotal) return;
        const isPk = !!(e?.colDef?.context?.pk ?? e?.colDef?.pk);
        // popupOnly(pk 아님)는 직접입력만 막은 "팝업으로 수정하는" 컬럼이라 기존행에서도 팝업이 열려야 함
        const isNonEditable = e?.colDef?.editable === false && !e?.colDef?.popupOnly;
        if ((isPk || isNonEditable) && e?.data?.cud !== 'C') return;
        eventHandlers?.onCellDoubleClicked?.(e);
      },
      // 2026-09-18: 화면이 setAgGridRows를 넘겼으면(옵트인) 화면의 onCellValueChanged(있다면 pk컬럼 중복체크
      // 등 화면별 검증만 수행 - 검증실패로 되돌림 처리까지 자기가 다 했으면 false를 반환)를 먼저 실행하고,
      // false가 아니면 setUpdateRow를 자동 호출해서 cud='U' 표기를 대신 해줌. 안 넘긴 화면은 기존처럼
      // eventHandlers.onCellValueChanged 안에서 직접 setUpdateRow를 호출해야 함(하위호환).
      onCellValueChanged: (e: any) => {
        // onCellValueChanged는 화면별로 boolean(false=자동표기 생략)을 반환할 수 있게 opt-in 확장했지만,
        // eventHandlers 전체의 타입(Record<string, (event:any)=>void>)은 다른 핸들러들과의 호환을 위해
        // 그대로 두고 여기서만 any로 반환값을 읽음.
        const customResult = (eventHandlers?.onCellValueChanged as ((event: any) => void | boolean) | undefined)?.(e);
        if (customResult === false) return;
        if (!setAgGridRows) return;
        setUpdateRow(e.api, setAgGridRows, e);
      },
    };
  }, [callPgmId, gridId, eventHandlers, restoreGridLayout, agGridType?.type, handleCellContextMenu, setAgGridRows, clearRange, applyRange]);
  // popupOnly 컬럼의 팝업 아이콘 클릭 = 셀 더블클릭과 같은 처리(위 onCellDoubleClicked의 pk/cud 가드 포함).
  // 컬럼정의(setAgGridCols)는 이 핸들러가 바뀔 때마다 다시 만들지 않도록 ref로 최신 핸들러만 넘김
  const popupDblClickRef = useRef<((e: any) => void) | undefined>(undefined);
  popupDblClickRef.current = mergedEventHandlers.onCellDoubleClicked;

  // 언마운트(탭 전환 등으로 그리드가 사라질 때) 시 레지스트리에서 제거
  //  - 이 화면(callPgmId)의 그리드가 전부 사라지면(탭을 완전히 닫음) activeGridByProgram도 같이 지워서,
  //    나중에 같은 화면을 다시 열었을 때 "처음 열림"으로 인식해 agGrid1이 기본 활성으로 표시되게 함.
  useEffect(() => {
    return () => {
      unregisterGrid(callPgmId, gridId);
      if (callPgmId && getRegisteredGrids(callPgmId).length === 0) {
        activeGridByProgram.delete(callPgmId);
      }
    };
  }, [callPgmId, gridId]);

  // agGridCols에 추가 컬럼을 동적으로 삽입 - preSaveColumns/sysSaveColumns의 헤더는 공통라벨(ddKey)로 번역
  const setAgGridCols = useMemo<(ColDef | ColGroupDef)[]>(() => {
     // 2026-09-10: type "I"(입력가능) 그리드의 No(순번) 컬럼 헤더에 'colRowNo'("No") 라벨을 붙이던 부분 주석처리.
     //   - type "Q"(조회전용) 그리드는 순번 헤더가 항상 빈 문자열인데 type "I"만 "No"가 표시되어 화면간 불일치 발생 -> 사용자 요청으로 비활성화.
     //   - 코드/라벨(colRowNo)은 유지하고 삭제하지 않음 - 필요시 아래 주석을 해제하면 복원 가능.
     const translatedPreSaveColumns = preSaveColumns.map(col =>
       // col.field === 'no' ? { ...col, headerName: ddKey('colRowNo') || col.headerName } : col
       col
     );
     const translatedSysSaveColumns = sysSaveColumns.map(col => {
       if (col.field === 'rowKey') return { ...col, headerName: ddKey('colKey') || col.headerName };
       if (col.field === 'login_user_id') return { ...col, headerName: ddKey('colLoginUserId') || col.headerName };
       return col;
     });
     // 2026-09-11: pk:true(Primary key) 컬럼은 editable을 화면에서 각각 코딩하지 않도록 공통으로 처리 -
     // 행추가(cud==='C') 상태에서만 입력 가능하고, 조회된 기존 행은 수정 불가로 고정.
     // 조회전용(agGridType==='Q') 그리드는 화면에서 실수로 editable/pk를 코딩해도 무조건 수정 불가로 강제.
     const isQueryOnly = agGridType?.type === 'Q';
     // 그룹 컬럼(children을 가진 ColDef)은 최상위 col에 editable:false를 줘도 하위 children에는
     // 적용되지 않아 그대로 편집 가능 상태로 남으므로, children도 재귀적으로 강제 처리해야 함.
     const forceNonEditable = (col: any): any => {
       if (col.children) return { ...col, editable: false, children: col.children.map(forceNonEditable) };
       return { ...col, editable: false };
     };
     // 2026-09-28: numericColumn 타입 컬럼의 헤더 가운데정렬 - 예전엔 columnTypes.numericColumn 재정의로
     // 처리했으나, ag-Grid가 내장 타입 재정의를 막아서(경고 #34) 조용히 무시되고 있었음(실제 헤더는 ag-Grid
     // 기본값인 우측정렬로 나가고 있었음). 컬럼 colDef 자체에 headerClass를 직접 박아서 우회함(개별 컬럼이
     // headerClass를 이미 지정했으면 그건 안 건드림 - 개별 지정이 항상 우선인 기존 규칙 유지).
     const fixNumericHeaderClass = (col: any): any => {
       const withChildren = col.children ? { ...col, children: col.children.map(fixNumericHeaderClass) } : col;
       const isNumeric = withChildren.type === 'numericColumn' || (Array.isArray(withChildren.type) && withChildren.type.includes('numericColumn'));
       if (!isNumeric || withChildren.headerClass !== undefined) return withChildren;
       return { ...withChildren, headerClass: 'ag-header-center' };
     };
     // 2026-09-16: colDef.align('left'|'center'|'right')/valign('top'|'center'|'bottom')로 셀 값 정렬을
     // 지정할 수 있게 지원 - ag-Grid ColDef에는 원래 없는 속성이라 화면에서 써도 조용히 무시되던 문제가
     // 있었음.
     //   - 처음엔 .ag-cell 자체에 display:flex(justifyContent/alignItems)를 cellStyle로 주는 방식을
     //     썼으나, No컬럼처럼 순수 cellClass(text-align)로 정렬하던 컬럼이 깨지는 회귀가 있었고(1차),
     //     opt-in(align/valign을 실제로 쓴 컬럼에만 적용)으로 좁혀도 여전히 안 먹히는 경우가 있었음(2차) -
     //     ag-Grid가 값 렌더링을 내부적으로 감싸는 방식(.ag-cell-wrapper 등)에 따라 .ag-cell 자체의
     //     flex 설정이 실제 값 위치에 반영 안 될 수 있어 신뢰할 수 없었음.
     //   - 그래서 값 자체를 우리가 직접 그리는 cellRenderer로 확실하게 제어함(이미 커스텀 cellRenderer가
     //     있는 컬럼은 각자 알아서 정렬한다고 보고 건드리지 않음). valueFormatter가 있으면 그 결과를 그대로 표시.
     const JUSTIFY_CONTENT_MAP: Record<string, string> = { left: 'flex-start', center: 'center', right: 'flex-end' };
     const ALIGN_ITEMS_MAP: Record<string, string> = { top: 'flex-start', center: 'center', bottom: 'flex-end' };
     // align이 적용되는 건 위 cellRenderer(보기모드 값 표시)뿐이라, 편집모드로 들어가면 ag-Grid가 이
     // cellRenderer를 걷어내고 자기 기본 <input>으로 바꿔버려서 정렬이 안 맞았음(2026-09-27). cellClass로
     // ag-cell-text-align-*를 같이 붙여서 AgGridV1.css의 "그 클래스 + input" 규칙이 편집모드에도 먹히게 함.
     const ALIGN_CELL_CLASS_MAP: Record<string, string> = { left: 'ag-cell-text-align-left', center: 'ag-cell-text-align-center', right: 'ag-cell-text-align-right' };
     const applyAlign = (col: any): any => {
       const withChildren = col.children ? { ...col, children: col.children.map(applyAlign) } : col;
       // 2026-09-28: align/valign은 ag-Grid 표준 colDef 속성이 아니라서 "invalid colDef property" 콘솔경고가
       // 남는 문제가 있었음 - ag-Grid 권장대로 colDef.context에 넣는 신규 방식(context.align/context.valign)도
       // 같이 지원함(우선순위: context 먼저, 없으면 기존 최상위 align/valign - 기존 화면은 안 건드려도 그대로 동작).
       const rawAlign = withChildren.context?.align ?? withChildren.align;
       const rawValign = withChildren.context?.valign ?? withChildren.valign;
       if (rawAlign === undefined && rawValign === undefined) return withChildren;
       if (withChildren.cellRenderer) return withChildren; // 이미 커스텀 렌더러가 있으면 그대로 둠(각자 정렬 책임)

       const alignKey: string = rawAlign ?? (withChildren.type === 'numericColumn' ? 'right' : 'left');
       const valignKey: string = rawValign ?? 'center';
       const justifyContent = JUSTIFY_CONTENT_MAP[alignKey] ?? 'flex-start';
       const alignItems = ALIGN_ITEMS_MAP[valignKey] ?? 'center';
       const existingFormatter = withChildren.valueFormatter;
       const alignCellClass = ALIGN_CELL_CLASS_MAP[alignKey];
       const existingCellClass = withChildren.cellClass;
       const mergedCellClass = Array.isArray(existingCellClass)
         ? [...existingCellClass, alignCellClass]
         : existingCellClass
         ? [existingCellClass, alignCellClass]
         : [alignCellClass];

       // ag-grid-react는 cellRenderer로 넘긴 함수를 React 함수형 컴포넌트로 취급해서 호출하므로(순수
       // ag-Grid Community처럼 원시 DOM 노드를 그대로 반환하면 "Objects are not valid as a React
       // child" 류 런타임 에러가 남) 반드시 JSX(React 엘리먼트)를 반환해야 함.
       // height:'100%'는 ag-grid-react가 cellRenderer 결과를 감싸는 중간 래퍼(.ag-react-container 등)에
       // 높이가 명시돼 있지 않으면 0/auto로 무너져서 alignItems(세로정렬)가 눈에 안 보이는 문제가 있어
       // rowHeight(행 높이)를 px로 직접 박아 확실한 기준 높이를 줌.
       // (3차) 그래도 안 먹혔던 진짜 원인: mergedDefaultColDef가 기본 세로중앙정렬을 위해 .ag-cell에
       // cellStyle: { lineHeight: `${rowHeight}px` }를 주는데, 이 line-height가 그대로 상속되어 이
       // div 안의 텍스트 자체가 rowHeight 높이의 line box를 갖게 됨 -> 텍스트(익명 flex 아이템)가 이미
       // flex 컨테이너 높이를 꽉 채운 상태가 돼서 alignItems가 옮길 여유 공간이 없었음. lineHeight를
       // normal로 재설정해서 텍스트 자체 높이를 폰트 기본값으로 되돌려야 flex-start/flex-end가 보임.
       const cellRenderer = (params: any) => {
         const text = existingFormatter ? existingFormatter(params) : (params.value ?? '');
         return (
           <div style={{ display: 'flex', width: '100%', height: rowHeight, lineHeight: 'normal', justifyContent, alignItems, overflow: 'hidden', textOverflow: 'ellipsis' }}>
             {text == null ? '' : String(text)}
           </div>
         );
       };

       return { ...withChildren, cellRenderer, cellClass: mergedCellClass };
     };
     // 2026-10-05: popupOnly(2026-10-17 popupInput도) 컬럼은 셀 오른쪽에 팝업 아이콘(합계/그룹 행 제외) - 기존 셀 표시(정렬 renderer 등)는 그대로 두고 덧붙임.
     // 아이콘 클릭 = 셀 더블클릭(popupDblClickRef -> 공통 가드 -> 화면 onCellDoubleClicked). 문자열로 등록된 renderer는 감쌀 수 없어 그대로 둠.
     const applyPopupIcon = (col: any): any => {
       if (col.children) return { ...col, children: col.children.map(applyPopupIcon) };
       if (!(col.popupOnly || col.popupInput) || typeof col.cellRenderer === 'string') return col;
       const Orig = col.cellRenderer;
       const cellRenderer = (params: any) => {
         const content = Orig
           ? <Orig {...params} />
           : <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{String(params.valueFormatted ?? params.value ?? '')}</span>;
         if (isSynthRow(params.data)) return content;
         return (
           <div style={{ width: '100%', height: '100%', paddingRight: 18, boxSizing: 'border-box' }}>
             {content}
             <PopupCellIcon onOpen={(ev) => popupDblClickRef.current?.({
               type: 'cellDoubleClicked', event: ev, colDef: params.colDef, column: params.column, data: params.data,
               node: params.node, api: params.api, value: params.value, rowIndex: params.node?.rowIndex, context: params.context,
             })} />
           </div>
         );
       };
       return { ...col, cellRenderer };
     };
     const pkAwareAgGridCols = agGridCols.map(col => {
       const aligned = applyPopupIcon(applyAlign(fixNumericHeaderClass(col)));
       if (isQueryOnly) return forceNonEditable(aligned);
       if (aligned.popupOnly) return { ...aligned, editable: false }; // pk 여부와 무관하게 직접입력 불가 - 팝업으로만 채움
       if (!(aligned.context?.pk ?? aligned.pk)) return aligned;
       return { ...aligned, editable: (params: any) => params.data?.cud === 'C' };
     });
     return [
       ...(agGridType?.type === "Q" ? preQueryColumns : translatedPreSaveColumns),
       ...pkAwareAgGridCols,
       ...(agGridType?.type === "I" ? translatedSysSaveColumns : [])
     ] as (ColDef | ColGroupDef)[];
  }, [agGridCols, agGridType?.type, ddKey, rowHeight]);

  // columnDefs가 바뀔 때마다(콤보/라벨 비동기 로딩 등) ag-Grid는 컬럼 순서/너비를 정의값으로 되돌리므로(숨김만 유지됨),
  // 사용자가 저장한(복원한) 레이아웃이 있으면 바뀐 정의가 반영된 직후 순서/너비/고정을 다시 적용함. 저장한 레이아웃이 없으면 아무것도 안 함.
  useEffect(() => {
    const reapply = () => {
      const api = agGridRef.current?.api;
      const state = api ? savedLayoutByApi.get(api) : undefined;
      if (api && state) api.applyColumnState({ state, applyOrder: true });
    };
    reapply();
    const t = window.setTimeout(reapply, 0); // ag-Grid가 변경된 정의를 한 틱 늦게 반영하는 경우 대비
    return () => window.clearTimeout(t);
  }, [setAgGridCols, agGridRef]);


  // 이후 AgGridReact에 setAgGridCols 사용
  /////////////////////////////////////////////////////////////////////////////////////////////////////////
  // 컬럼명(헤더) 가운데정렬 공통 기본값
  //  - defaultColDef : 일반 컬럼 헤더 기본 가운데정렬 (화면별 agGridColsDefault보다 먼저 적용, 화면에서 headerClass를 직접 지정하면 그게 우선)
  //  - numericColumn 타입 컬럼의 헤더만 가운데정렬(데이터 셀은 기존처럼 우측정렬 유지)하려던 게 목적이었는데,
  //    예전엔 columnTypes.numericColumn을 재정의해서 처리했었음. 2026-09-28: ag-Grid가 내장 컬럼타입
  //    (numericColumn 등)을 columnTypes로 덮어쓰는 걸 막기 시작해서(경고 #34), 그 재정의가 조용히
  //    무시되고 있었던 걸 발견함(실제로는 헤더가 ag-Grid 기본값인 우측정렬로 돌아가 있었음 - 겉보기 경고인
  //    줄 알았는데 진짜 기능 회귀였음). 그래서 columnTypes 재정의는 지우고, 아래 setAgGridCols에서 각
  //    numericColumn 컬럼 colDef 자체에 headerClass를 직접 박아주는 방식(fixNumericHeaderClass)으로 교체함
  //    - 개별 컬럼이 headerClass를 이미 지정했으면 그게 우선(안 건드림).
  /////////////////////////////////////////////////////////////////////////////////////////////////////////
  // 2026-09-27: ag-Grid v32.2부터 rowSelection에 문자열("single"/"multiple")을 주는 옛 방식과
  // suppressRowClickSelection이 deprecated돼서 객체 방식으로 변경. 매 렌더마다 새 객체 리터럴을 그대로
  // 넘기면(참조가 매번 바뀜) ag-Grid가 "옵션이 바뀌었다"고 판단해 재처리하는 게 반복되면서 AgGridColsChooser의
  // useEffect가 다시 불려 무한루프(Maximum update depth exceeded)에 빠졌던 문제 - 원시값(rowSelection?.type/
  // suppressRowClickSelection)만 의존성으로 삼아 참조를 고정함.
  const rowSelectionOptions = useMemo(() => ({
    // checkboxes는 새 API 기본값이 true라서 명시적으로 false를 줘야 함(이 프로젝트는 체크박스 없이
    // No.컬럼 클릭으로 행선택하는 방식이라, 안 그러면 모든 그리드에 체크박스 컬럼이 갑자기 생겨버림).
    mode: (rowSelection?.type === 'single' ? 'singleRow' : 'multiRow') as 'singleRow' | 'multiRow',
    checkboxes: false,
    // headerCheckbox("전체선택" 체크박스)는 checkboxes와 별개 옵션이라 따로 꺼야 함 - 기본값이 true라서
    // checkboxes:false만 주면 행 체크박스는 안 보여도 헤더에 "전체선택" 체크박스만 혼자 남아있었음.
    headerCheckbox: false,
    enableClickSelection: !suppressRowClickSelection, // 행 클릭 시 선택 비활성화 (false-활성화, true-비활성화)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  } as any), [rowSelection?.type, suppressRowClickSelection]);

  const mergedDefaultColDef = useMemo<ColDef>(() => ({
    headerClass: 'ag-header-center',
    headerComponentParams: { innerHeaderComponent: GridHeaderPinToggle },
    // 2026-09-16: 행 높이를 기본(30px)보다 키운 화면에서 값이 위쪽에 붙어 보이지 않도록 세로 중앙정렬을
    // 공통 기본값으로 적용 - display:flex 방식은 justify-content 기본값이 flex-start라 기존
    // text-align(ag-cell-text-align-center/right, numericColumn의 ag-right-aligned-cell 등)으로 하던
    // 가로정렬을 전부 좌측정렬로 깨뜨려서(No컬럼이 갑자기 좌측정렬되던 원인) line-height 방식으로 교체함
    // - 한 줄 텍스트를 lineHeight=rowHeight로 맞춰서 세로중앙처럼 보이게 하되 text-align은 그대로 살림.
    // 화면에서 agGridColsDefault(또는 개별 컬럼)에 cellStyle을 직접 지정하면 그게 우선(아래 ...agGridColsDefault).
    cellStyle: { lineHeight: `${rowHeight}px` },
    // row-created/updated/deleted는 gridOptions.rowClassRules로 .ag-row에도 적용되지만,
    // text-decoration(취소선)은 색상과 달리 자식 셀에 자동으로 전파되지 않아 각 셀에도 동일하게 걸어줌.
    cellRendererSelector: agGridType?.type === 'Q' ? subtotalCellRendererSelector : undefined,   // 소계/합계 행 전용 셀 표시(조회전용 그리드만 - 일반 행은 컬럼 자신의 renderer/기본 표시 그대로)
    cellClassRules: {
      // 범위 선택 복사 - 드래그로 잡은 사각형 범위(2셀 이상일 때만)
      'ag-cell-range': (params: any) => {
        if (cellSetRef.current.size) return cellSetRef.current.has(params.node?.rowIndex + '|' + params.column?.getColId?.());   // Ctrl+클릭 셀 다중선택
        const R = rangeRef.current;
        if (!R || R.rowMode || (R.rowMin === R.rowMax && R.colIds.size <= 1)) return false;   // rowMode(No 클릭)는 행 선택 색으로 표시
        const i = params.node?.rowIndex;
        return i != null && i >= R.rowMin && i <= R.rowMax && R.colIds.has(params.column?.getColId?.());
      },
      // 2026-09-11: 조회전용(agGridType==='Q') 그리드는 애초에 cud가 생기지 않지만, 화면 코딩 실수에 대비해
      // 행추가/수정/필수 표시 관련 셀 스타일을 전부 무력화(안전장치).
      'row-created': (params: any) => agGridType?.type !== 'Q' && params.data?.cud === 'C',
      'row-updated': (params: any) => agGridType?.type !== 'Q' && params.data?.cud === 'U',
      'row-deleted': (params: any) => agGridType?.type !== 'Q' && params.data?.cud === 'D',
      // required 컬럼 표시 - pk(Primary key) 컬럼은 required:true를 안 붙여도 자동으로 필수 취급되어
      // 행추가(cud==='C') 행만 연노란색(조회된 기존 행은 무색), pk가 아닌 required 컬럼은 행 상태와 상관없이 항상 연노란색.
      'ag-cell-required': (params: any) => {
        if (agGridType?.type === 'Q') return false;
        const isPk = !!(params.colDef?.context?.pk ?? params.colDef?.pk);
        const isRequired = params.colDef?.context?.required ?? params.colDef?.required;
        if (!isRequired && !isPk) return false;
        if (isPk) return params.data?.cud === 'C';
        return true;
      },
    },
    ...agGridColsDefault,
  }), [agGridColsDefault, agGridType?.type, rowHeight]);

  /////////////////////////////////////////////////////////////////////////////////////////////////////////
  // gridOptions (rowClassRules 기반 스타일)
  /////////////////////////////////////////////////////////////////////////////////////////////////////////
  const gridOptions: GridOptions = useMemo(
    () => ({
      rowClassRules: {
        "row-created": (params) => params.data?.cud === "C",
        "row-updated": (params) => params.data?.cud === "U",
        "row-deleted": (params) => params.data?.cud === "D",
        "aggrid-group-row": (params) => !!params.data?.__group,
        "aggrid-subtotal-row": (params) => !!params.data?.__subtotal && params.data?.__key !== '__total',
        "aggrid-total-row": (params) => !!params.data?.__subtotal && params.data?.__key === '__total',
        ...(extraRowClassRules ?? {}),
      },
      ...(getRowStyle ? { getRowStyle } : {}),   // 2026-10-10: 행별 인라인 스타일(ZM901M1 조건부 서식 ROW 규칙 등) - 안 넘기면 기존처럼 미지정
      defaultCsvExportParams: { shouldRowBeSkipped: (p: any) => !!p.node?.data?.__group }, // 컬럼 그룹핑 헤더행은 엑셀/CSV에서 제외
      animateRows: true,             // 행 추가/삭제 시 애니메이션 효과
      alwaysShowVerticalScroll: true, // 세로 스크롤 항상 표시
      enableCellTextSelection: true,  // 셀 내 텍스트 선택 허용
      singleClickEdit: true,   // 셀 클릭 한번으로 수정모드 진입(사용자 요청으로 true로 변경 - 과거 엣지 이슈 있었으나 재검토 후 전환)
      stopEditingWhenCellsLoseFocus: true, // 셀 포커스 잃으면 편집 종료
      suppressClickEdit: false, // 셀 클릭 시 편집 모드 진입 허용 (false-허용, true-미허용)
      headerHeight: 40,
      rowHeight,
      rowBuffer: 0,            // 화면 밖 행 렌더링 비활성화
      theme: "legacy",
      domLayout: 'normal', // 전체 높이 자동 조정, 'autoHeight', // 그리드 높이를 내용에 맞게 자동 조정
      // sideBar removed
      }),
    [rowHeight, extraRowClassRules, getRowStyle]
  );
//console.log('eventHandlers', eventHandlers);
  // 높이 클래스 매핑: 인라인 스타일 사용 금지 정책 대응
  const getHeightClass = (h?: string | number) => {
    if (typeof h === 'string') {
      const v = h.trim().toLowerCase();
      if (v === '100%') return 'h-100p';
      if (v === '20vh') return 'h-20vh';
      if (v === '45vh') return 'h-45vh';
      if (v === '65vh') return 'h-65vh';
    }
    return 'h-65vh';
  };
  const containerClass = `ag-theme-alpine ag-grid-wrapper ${getHeightClass(height)} ${isActiveGrid ? 'ag-grid-active-border' : ''} ${multiSelected ? 'aggrid-multi-selected' : ''}`;
  return (
    <div
      ref={wrapperRef}
      className={containerClass}
      onKeyDown={handleRangeKeyDown}   // 범위 선택 복사(Ctrl+C) / 해제(Esc)
      onPaste={handlePasteEvent}   // 클립보드(엑셀 등)에서 복사한 값을 포커스된 셀부터 붙여넣기(Ctrl+V)
      // 그리드 위에서는 브라우저 기본 우클릭 메뉴(새로고침/뒤로가기 등) 대신 자체 메뉴를 표시
      onContextMenu={handleContextMenu}
      // 데이터가 없어 포커스할 셀이 없는 그리드도 클릭만으로 활성화되도록 하는 보완 경로(위 주석 참고)
      onMouseDownCapture={handleGridMouseDown}
      onClickCapture={handleGridClickCapture}
    >
      {rangeTip && (
        <div style={{ position: 'fixed', left: rangeTip.x + 14, top: rangeTip.y + 18, zIndex: 1500, pointerEvents: 'none', background: 'rgba(30,41,59,0.94)', color: '#fff', fontSize: 12, lineHeight: 1.5, padding: '4px 10px', borderRadius: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.25)', whiteSpace: 'nowrap' }}>
          {rangeTip.sum != null && (
            <div>{`${(ddKey('lblSum') || '합계') + (rangeTip.sumPartial ? (ddKey('lblSumNumericCols') || '(숫자)') : '')}: ${rangeTip.sum.toLocaleString('ko-KR', { maximumFractionDigits: 4 })}`}</div>
          )}
          {rangeTip.multiCol && <div>{`${ddKey('lblRowCount') || '행개수'}: ${rangeTip.rows.toLocaleString('ko-KR')}`}</div>}
          <div>{`${ddKey('lblCount') || '개수'}: ${rangeTip.count.toLocaleString('ko-KR')}`}</div>
        </div>
      )}
      {/* 좌측 상단 모서리 톱니 버튼 - 숨긴 컬럼을 포함한 전체 컬럼 목록 팝업(체크박스로 표시/숨김 토글) */}
      <AgGridColsChooser
        gridRef={agGridRef as any}
        title={ddKey('lblColumnChooser') || '컬럼 선택'}
        onSaveLayout={callPgmId && gridId ? () => { saveGridLayout(callPgmId, gridId, (agGridRef as any)?.current?.api, menuId || pgmIdOf(callPgmId)); } : undefined}
      />
      <AgGridReact<T>
        key={language} // 언어가 바뀔 때마다 강제 리렌더링
        ref={agGridRef}
        rowData={groupedRows ?? agGridRows}
        // rowData 배열은 셀 편집/행추가삭제복사취소마다 새 참조로 바뀌는데(setUpdateRow 등),
        // getRowId 없이는 ag-Grid가 이를 "완전히 새 데이터"로 취급해 스크롤/선택 상태를 전부 리셋함
        // (맨 아래 행 편집 후 다른 행 클릭 시 스크롤이 맨 위로 튀는 원인). rowKey는 이미 setInsertRow/
        // setUpdateRow/setDeleteRow/setCopyRow/setCancelRow 전부가 행 식별자로 쓰는 값이라 그대로 사용.
        getRowId={(params: any) => params.data?.__group ? `__g:${params.data.__key}` : params.data?.__subtotal ? `__s:${params.data.__key}` : String(params.data?.rowKey ?? params.data?.no)}
        isFullWidthRow={(params: any) => !!params.rowNode?.data?.__group}
        fullWidthCellRenderer={GroupRow}
        fullWidthCellRendererParams={fullWidthCellRendererParams}
        columnDefs={setAgGridCols}
        defaultColDef={mergedDefaultColDef}
        pagination={pagination && !groupedRows}
        paginationPageSize={pageSize}
        paginationPageSizeSelector={stablePageSizeSelector}
        rowSelection={rowSelectionOptions}
        overlayNoRowsTemplate={ddKey('msgNoDataRows') || '데이터가 없습니다'}
        {...gridOptions}
        {...mergedEventHandlers}  // 이벤트 핸들러 객체(그리드 레이아웃 복원 로직 포함)
        
      />
      {loading && (
        <div className="aggrid-loading-overlay">
          <i className="fas fa-spinner fa-spin aggrid-loading-icon" />
        </div>
      )}
      <Menu
        open={!!contextMenu}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={contextMenu ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              minWidth: 190,
              borderRadius: 2,
              boxShadow: '0 8px 24px rgba(0,0,0,0.16)',
              p: 0.5,
              overflow: 'visible', // "그리드위치" CSS 호버 플라이아웃(gridPosFlyoutSx)이 Paper 밖으로 나가므로 잘리지 않게
            },
          },
          list: { sx: { py: 0, overflow: 'visible' } },
        }}
      >
        {/* 드래그로 잡은 블록의 집계(정보 표시 전용) - 숫자컬럼만 선택했으면 합계+개수, 그 외는 개수만 */}
        {blockStats && (
          <>
            {blockStats.sum != null && (
              <MenuItem disableRipple sx={{ ...blockSx(0), cursor: 'default', pointerEvents: 'none', fontWeight: 600 }}>
                <i className="fas fa-calculator w-4" style={{ fontSize: 14 }}></i>
                {`${(ddKey('lblSum') || '합계') + (blockStats.sumPartial ? (ddKey('lblSumNumericCols') || '(숫자)') : '')}: ${blockStats.sum.toLocaleString('ko-KR', { maximumFractionDigits: 4 })}`}
              </MenuItem>
            )}
            {blockStats.multiCol && (
              <MenuItem disableRipple sx={{ ...blockSx(0), cursor: 'default', pointerEvents: 'none', fontWeight: 600 }}>
                <i className="fas fa-grip-lines w-4" style={{ fontSize: 14 }}></i>
                {`${ddKey('lblRowCount') || '행개수'}: ${blockStats.rows.toLocaleString('ko-KR')}`}
              </MenuItem>
            )}
            <MenuItem disableRipple sx={{ ...blockSx(0), cursor: 'default', pointerEvents: 'none', fontWeight: 600 }}>
              <i className="fas fa-hashtag w-4" style={{ fontSize: 14 }}></i>
              {`${ddKey('lblCount') || '개수'}: ${blockStats.count.toLocaleString('ko-KR')}`}
            </MenuItem>
            <Divider sx={{ my: 0.25 }} />
          </>
        )}
        {canCopy && (
          <>
            <MenuItem onClick={() => handleContextCopy(false)} sx={blockSx(1)}>
              <i className="fas fa-copy w-4" style={{ fontSize: 14 }}></i>
              {ddKey('btnCopyCells') || '복사'}
            </MenuItem>
            <MenuItem onClick={() => handleContextCopy(true)} sx={blockSx(1)}>
              <i className="fas fa-table-list w-4" style={{ fontSize: 14 }}></i>
              {ddKey('btnCopyCellsHeader') || '복사(헤더포함)'}
            </MenuItem>
            <Divider sx={{ my: 0.25 }} />
          </>
        )}
        {/* "그리드위치" 하위메뉴 - 저장/초기화(이 그리드만) + 전체저장/전체취소(2026-10-07, 2개 블록을 1개로 합침)
            2026-10-08: MUI Menu를 중첩하면(별도 Popover) 투명 백드롭이 마우스 이벤트를 가로채서
            "벗어남<->진입"이 반복되는 깜빡임이 생김 - 순수 CSS(:hover로 표시/숨김) 방식으로 전환,
            별도 Popover/상태/타이머 없이 이 MenuItem의 자식으로 직접 배치(position:absolute). */}
        <MenuItem sx={{ ...blockSx(2), position: 'relative', '&:hover > .gridPosFlyout': { display: 'block' } }}>
          <i className="fas fa-layer-group w-4" style={{ fontSize: 14 }}></i>
          {ddKey('btnGridPos') || '그리드위치'}
          <i className="fas fa-chevron-right" style={{ fontSize: 12, marginLeft: 'auto', paddingLeft: 12 }}></i>
          <Box className="gridPosFlyout" sx={gridPosFlyoutSx}>
            {/* 2026-10-08: 바깥 "그리드위치" MenuItem(li)의 자식이라 안쪽도 MenuItem(li)을 쓰면
                <li> 안에 <li>가 들어가는 잘못된 HTML 중첩 경고가 남 - role만 menuitem인 Box(div)로 대체 */}
            <Box role="menuitem" tabIndex={-1} onClick={handleContextSaveGrid} sx={contextMenuItemSx}>
              <i className="fas fa-thumbtack w-4" style={{ fontSize: 14 }}></i>
              {ddKey('btnSetGrid') || '그리드위치저장'}
            </Box>
            <Box role="menuitem" tabIndex={-1} onClick={handleContextResetGrid} sx={contextMenuItemSx}>
              <i className="fas fa-undo w-4" style={{ fontSize: 14 }}></i>
              {ddKey('btnResetGrid') || '그리드위치취소'}
            </Box>
            <Divider sx={{ my: 0.25 }} />
            <Box role="menuitem" tabIndex={-1} onClick={handleContextSaveGridAll} sx={contextMenuItemSx}>
              <i className="fas fa-layer-group w-4" style={{ fontSize: 14 }}></i>
              {ddKey('btnSetGridAll') || '그리드위치저장(전체)'}
            </Box>
            <Box role="menuitem" tabIndex={-1} onClick={handleContextResetGridAll} sx={contextMenuItemSx}>
              <i className="fas fa-eraser w-4" style={{ fontSize: 14 }}></i>
              {ddKey('btnResetGridAll') || '그리드위치취소(전체)'}
            </Box>
          </Box>
        </MenuItem>
        {/* "그리드위치(A)" 하위메뉴 - 관리자용, USER_ID='*'(전체 공통 기본값)으로 저장/취소 (2026-10-07)
            ZA_USER.USER_KIND='SA'(시스템마스터)인 계정에만 노출 - 나머지는 숨김 */}
        {isSysAdmin && (
        <MenuItem sx={{ ...blockSx(2), position: 'relative', '&:hover > .gridPosFlyout': { display: 'block' } }}>
          <i className="fas fa-layer-group w-4" style={{ fontSize: 14 }}></i>
          {ddKey('btnGridPosAdmin') || '그리드위치(A)'}
          <i className="fas fa-chevron-right" style={{ fontSize: 12, marginLeft: 'auto', paddingLeft: 12 }}></i>
          <Box className="gridPosFlyout" sx={gridPosFlyoutSx}>
            <Box role="menuitem" tabIndex={-1} onClick={handleContextSaveGridAdmin} sx={contextMenuItemSx}>
              <i className="fas fa-thumbtack w-4" style={{ fontSize: 14 }}></i>
              {ddKey('btnSetGridAdmin') || '그리드위치저장'}
            </Box>
            <Box role="menuitem" tabIndex={-1} onClick={handleContextResetGridAdmin} sx={contextMenuItemSx}>
              <i className="fas fa-undo w-4" style={{ fontSize: 14 }}></i>
              {ddKey('btnResetGridAdmin') || '그리드위치취소'}
            </Box>
            <Divider sx={{ my: 0.25 }} />
            <Box role="menuitem" tabIndex={-1} onClick={handleContextSaveGridAllAdmin} sx={contextMenuItemSx}>
              <i className="fas fa-layer-group w-4" style={{ fontSize: 14 }}></i>
              {ddKey('btnSetGridAllAdmin') || '그리드위치저장(전체)'}
            </Box>
            <Box role="menuitem" tabIndex={-1} onClick={handleContextResetGridAllAdmin} sx={contextMenuItemSx}>
              <i className="fas fa-eraser w-4" style={{ fontSize: 14 }}></i>
              {ddKey('btnResetGridAllAdmin') || '그리드위치취소(전체)'}
            </Box>
            {/* [컬럼속성보기] - 우클릭한 컬럼의 컬럼ID/컬럼명/타입 등을 간단한 메시지박스로 표시(2026-10-10).
                셀/헤더 우클릭처럼 대상 컬럼이 있을 때만 노출(빈 공간 우클릭이면 숨김). */}
            {(contextMenu?.field || contextMenu?.groupCol) && (
              <>
                <Divider sx={{ my: 0.25 }} />
                <Box role="menuitem" tabIndex={-1} onClick={handleContextShowColumnInfo} sx={contextMenuItemSx}>
                  <i className="fas fa-circle-info w-4" style={{ fontSize: 14 }}></i>
                  {ddKey('btnShowColumnInfo') || '컬럼속성보기'}
                </Box>
              </>
            )}
          </Box>
        </MenuItem>
        )}

        <Divider sx={{ my: 0.25 }} />

        {/* 셀 우클릭(contextMenu.field 존재)일 때만 표시 - 해당 컬럼의 필터만 초기화 */}
        {contextMenu?.field && (
          <MenuItem onClick={handleContextClearColumnFilter} sx={blockSx(3)}>
            <i className="fas fa-filter w-4" style={{ fontSize: 14 }}></i>
            {ddKey('btnClearColumnFilter') || '필터지우기'}
          </MenuItem>
        )}
        <MenuItem onClick={handleContextClearAllFilters} sx={blockSx(3)}>
          <i className="fas fa-filter-circle-xmark w-4" style={{ fontSize: 14 }}></i>
          {ddKey('btnClearAllFilters') || '필터지우기(전체)'}
        </MenuItem>
        {/* 컬럼 그룹핑 - 조회전용(Q) 그리드만: 우클릭한 컬럼을 그룹 단계로 추가(다단계) / 마지막 단계 해제 / 전체 해제 */}
        {agGridType?.type === 'Q' && ((groupMenuCol && !groupFields.includes(groupMenuCol)) || groupFields.length > 0) && <Divider sx={{ my: 0.25 }} />}
        {agGridType?.type === 'Q' && groupMenuCol && !groupFields.includes(groupMenuCol) && (
          <MenuItem onClick={handleContextGroupBy} sx={blockSx(4)}>
            <i className="fas fa-object-group w-4" style={{ fontSize: 14 }}></i>
            {(groupFields.length ? (ddKey('btnGroupByAdd') || '그룹핑 단계 추가') : (ddKey('btnGroupBy') || '컬럼 그룹핑'))}{`: ${findColDef(groupMenuCol)?.headerName || groupMenuCol}`}
          </MenuItem>
        )}
        {agGridType?.type === 'Q' && groupFields.length > 1 && (
          <MenuItem onClick={handleContextUngroupLast} sx={blockSx(4)}>
            <i className="fas fa-outdent w-4" style={{ fontSize: 14 }}></i>
            {(ddKey('btnUngroupLast') || '마지막 단계 해제')}{`: ${findColDef(groupFields[groupFields.length - 1])?.headerName || groupFields[groupFields.length - 1]}`}
          </MenuItem>
        )}
        {agGridType?.type === 'Q' && groupFields.length > 0 && (
          <MenuItem onClick={handleContextClearGroup} sx={blockSx(4)}>
            <i className="fas fa-object-ungroup w-4" style={{ fontSize: 14 }}></i>
            {ddKey('btnClearGroup') || '그룹핑 해제'}
          </MenuItem>
        )}
        {/* 우클릭한 셀의 컬럼(field)이 ZA_GRID_RMENU에 매핑되어 있을 때만 조건부로 표시 */}
        {matchedRMenu && (
          <>
            <Divider sx={{ my: 0.25 }} />
            <MenuItem onClick={handleOpenRefPopup} sx={{ ...blockSx(5), color: '#2563eb', fontWeight: 600, '&:hover': { backgroundColor: '#eff6ff' } }}>
              <i className="fas fa-address-card w-4" style={{ fontSize: 14 }}></i>
              {(matchedRMenu.titleKey && ddKey(matchedRMenu.titleKey)) || matchedRMenu.objId}
            </MenuItem>
          </>
        )}
      </Menu>
    </div>
  );
});

//////////////////////////////////////////////////////////////////////////////////////////////////////////
// 렌더링 완료 후 안전하게 첫 행 선택
///////////////////////////////////////////////////////////////////////////////////////////////////////////
export function selectFirstRow(
  agGrid: React.RefObject<AgGridReact<any> | null>,
  focusFirstCell: boolean = true
) {
  let attempts = 0;
  const maxAttempts = 40; // ~2s at 50ms interval

  const trySelect = (): void => {
    const api: any = agGrid.current?.api;
    if (!api) {
      if (attempts++ < maxAttempts) { setTimeout(trySelect, 50); return; }
      return;
    }
    const rowCount = api.getDisplayedRowCount ? api.getDisplayedRowCount() : 0;
    if (rowCount <= 0) {
      if (attempts++ < maxAttempts) { setTimeout(trySelect, 50); return; }
      return;
    }

    // Ensure index visible and select node deterministically
    api.ensureIndexVisible?.(0, 'top');
    const firstNode = api.getDisplayedRowAtIndex(0);
    if (!firstNode) {
      if (attempts++ < maxAttempts) { setTimeout(trySelect, 50); return; }
      return;
    }

    // Deselect others to avoid flicker and make selection deterministic
    api.deselectAll?.();
    if (!firstNode.isSelected?.()) firstNode.setSelected(true);

    if (focusFirstCell && api.getAllDisplayedColumns) {
      // ag-Grid v34+에서는 columnApi가 사라지고 컬럼 관련 메서드가 전부 api로 통합됨(columnApi는 항상 undefined였음
      // - 그래서 이 블록이 실제로는 한 번도 실행되지 않아 setFocusedCell()이 호출된 적이 없었음)
      const firstCol = api.getAllDisplayedColumns()[0];
      const colId = firstCol?.getColId?.();
      if (colId) {
        // focus and keep visible
        api.setFocusedCell?.(0, colId);
        api.ensureIndexVisible?.(0, 'top');
      }
    }
  };

  // Kick off on next macrotask for safety
  setTimeout(trySelect, 0);
}
/////////////////////////////////////////////////////////////////////////////////////////////////////////
// function : setInsertRow 행추가
// 설명     : 그리드에서 선택된 행을 {생성/삭제/복사/취소} 처리하기
// 파라미터 :
//   - gridApi: any - 그리드 API 객체
//   - setRows: (fn: (prev: any[]) => any[]) => void - 행 상태 업데이트 함수
//   - keyField: string - 행의 고유 키 필드명 (기본값: 'rowKey')
// 작성자   : 박동근
// 최종수정일 : 2025-10-11
/////////////////////////////////////////////////////////////////////////////////////////////////////////
export function setInsertRow(gridApi: any, setRows: (fn: (prev: any[]) => any[]) => void, newGridRowData?: any, keyField: string = 'rowKey') {
  // 2026-09-11: 조회전용(agGridType==='Q') 그리드는 행추가 무력화.
  if (isQueryOnlyGridApi(gridApi)) return;
  const selectedGrid = gridApi.getSelectedRows();
  /*행 추가 */
  setRows(prev => {
      // 신규행의 keyField 값 생성 - 서버 rowKey(양수)와 절대 겹치지 않도록 음수로 채번(nextTempKey 참고)
      const maxKey = nextTempKey();

      let insertIdx = prev.length;
      if (selectedGrid && selectedGrid.length > 0) {
          const selectedKey = selectedGrid[0][keyField];
          const idx = prev.findIndex(row => row[keyField] === selectedKey);
          if (idx !== -1) insertIdx = idx + 1;
      }
      const rowToInsert = { cud: 'C', no: 0, ...newGridRowData, [keyField]: maxKey };

      const nextRows = [
          ...prev.slice(0, insertIdx),
          rowToInsert,
          ...prev.slice(insertIdx)
      ];
      setTimeout(() => {
          if (gridApi) {
              gridApi.deselectAll();
              gridApi.forEachNode((node: any, i: number) => {
                  if (i === insertIdx) {
                      node.setSelected(true);
                      gridApi.ensureIndexVisible(i, 'middle');
                  }
              });
          }
      }, 0);
      return nextRows;
  });
}

/////////////////////////////////////////////////////////////////////////////////////////////////////////
// function : setUpdateRow 행수정
// 설명     : 그리드에서 선택된 행을 {생성/삭제/복사/취소} 처리하기
// 파라미터 :
//   - gridApi: any - 그리드 API 객체
//   - setRows: (fn: (prev: any[]) => any[]) => void - 행 상태 업데이트 함수
//   - e: any - 그리드 이벤트 객체
//   - keyField: string - 행의 고유 키 필드명 (기본값: 'rowKey')
// 작성자   : 박동근
// 최종수정일 : 2025-10-11
/////////////////////////////////////////////////////////////////////////////////////////////////////////
export function setUpdateRow(gridApi: any, setRows: (fn: (prev: any[]) => any[]) => void, e: any) {
    const { data, colDef, newValue } = e;
    // 2026-09-16: newValue === ''(빈 문자열로 지움)도 막던 것을 제거 - 필수아님 필드를 지워서 비우는 것도
    // 정상적인 수정이라 cud='U'로 잡혀야 하는데, 이 가드 때문에 빈 값으로 지우면 행이 계속 미변경으로 남는
    // 버그가 있었음(Z2001M1의 param 필드에서 발견). newValue === undefined(콜백 자체가 값 없이 불린 경우)만 막음.
    if (!data || !colDef || newValue === undefined) return;
    const keyField: string = 'rowKey';
    // 2026-09-12: colDef에 valueSetter가 있는 컬럼(checkboxColDef/dateColDef 등)은 e.newValue(valueGetter로
    // 재계산된 값 - 예: boolean, ISO날짜)와 실제 저장되는 값(data[field] - 예: 'Y'/'N', 압축형 날짜)이 다를 수
    // 있음. newValue를 그대로 쓰면 valueSetter가 방금 써놓은 값을 덮어써서 되돌아가 버리므로, 항상 valueSetter
    // 적용 이후의 실제값(data[colDef.field])을 사용함 - valueSetter가 없는 일반 컬럼은 둘이 항상 같아서 무관.
    const actualValue = data[colDef.field];
    setRows(prev =>
        prev.map(row =>
            row[keyField] === data[keyField]
                ? {
                    ...row,
                    [colDef.field]: actualValue,
                    //no: row.cud === '' || row.cud === undefined ? 'U' : row.cud,
                    cud: row.cud === 'C' ? 'C' : 'U',
                }
                : row
        )
    );
}

/////////////////////////////////////////////////////////////////////////////////////////////////////////
// function : setDeleteRow 행삭제
// 설명     : 그리드에서 선택된 행(멀티선택이면 선택된 모든 행)을 삭제 처리하기
// 파라미터 :
//   - gridApi: any - 그리드 API 객체
//   - setRows: (fn: (prev: any[]) => any[]) => void - 행 상태 업데이트 함수
//   - onFocusRow?: (row: any) => void - 삭제 후 포커스가 이동한 행을 알려주는 콜백(신규행(C) 삭제 시에만
//     실제로 다른 행으로 포커스가 이동함). setSelected()로 프로그램에 의해 선택되면 onRowClicked가 발생하지
//     않으므로, 이 행 기준으로 하위 그리드를 재조회해야 하는 화면에서 사용(setCancelRow와 동일한 용도).
// 작성자   : 박동근
// 최종수정일 : 2025-10-11
/////////////////////////////////////////////////////////////////////////////////////////////////////////
export function setDeleteRow(gridApi: any, setRows: (fn: (prev: any[]) => any[]) => void, onFocusRow?: (row: any) => void) {
  // 2026-09-11: 조회전용(agGridType==='Q') 그리드는 행삭제 무력화.
  if (isQueryOnlyGridApi(gridApi)) return;
  const selectedGrid = gridApi.getSelectedRows();

  if (!selectedGrid || selectedGrid.length === 0) return;
  // 2026-09-24: 멀티선택(2행 이상)이면 선택된 행 전체에 일괄 적용 - 신규행(C)은 제거, 그 외는 삭제표시(D)
  const delKeys = new Set<any>(selectedGrid.map((r: any) => r.rowKey));
  setRows(prev => {
      // 포커스 이동 기준 = 선택된 행 중 가장 위 행
      const delIdx = prev.findIndex(row => delKeys.has(row.rowKey));
      const curRows = prev.filter(row =>
          delKeys.has(row.rowKey)
              ? row.cud !== 'C'
                  ? true
                  : false
              : true
      ).map(row =>
          delKeys.has(row.rowKey) && row.cud !== 'C'
              ? { ...row, cud: 'D' }
              : row
      );
      setTimeout(() => {
        if (gridApi) {
          gridApi.deselectAll();
          let focusIdx = delIdx;
          // 삭제행이 신규행(C)이면 이전행으로 포커스 이동
          if (prev[delIdx]?.cud === 'C') {
              focusIdx = delIdx > 0 ? delIdx - 1 : 0;
          }
          gridApi.forEachNode((node: any, i: number) => {
              if (i === focusIdx && curRows[i]) {
                  node.setSelected(true);
                  gridApi.ensureIndexVisible(i, 'middle');
              }
          });
          onFocusRow?.(curRows[focusIdx]);
        }
      }, 0);
      return curRows;
  });
}
/////////////////////////////////////////////////////////////////////////////////////////////////////////
// function : setDeleteRow 행복사
// 설명     : 그리드에서 선택된 행을 {생성/삭제/복사/취소} 처리하기
// 파라미터 :
//   - gridApi: any - 그리드 API 객체
//   - setRows: (fn: (prev: any[]) => any[]) => void - 행 상태 업데이트 함수
//   - keyField: string - 행의 고유 키 필드명 (기본값: 'rowKey')
//   - rowInit?: Partial<any> - 복사된 행에 추가할 초기값
// 작성자   : 박동근
// 최종수정일 : 2025-10-11
/////////////////////////////////////////////////////////////////////////////////////////////////////////
export function setCopyRow(gridApi: any, setRows: (fn: (prev: any[]) => any[]) => void, keyField: string = 'rowKey', rowInit?: Partial<any>) {
  // 2026-09-11: 조회전용(agGridType==='Q') 그리드는 행복사 무력화.
  if (isQueryOnlyGridApi(gridApi)) return;
  const selectedGrid = gridApi.getSelectedRows();
  if (!selectedGrid || selectedGrid.length === 0) return;
  const src = selectedGrid[0];
  // 2026-09-11: pk:true 컬럼은 원본 키값을 그대로 복사하면 안 되므로(재입력 필요) 빈 값으로 초기화.
  let pkBlankFields: Record<string, string> = {};
  try {
    const colDefs = gridApi?.getColumnDefs?.() || [];
    pkBlankFields = colDefs.reduce((acc: Record<string, string>, col: any) => {
      if ((col?.context?.pk ?? col?.pk) && col?.field) acc[col.field] = '';
      return acc;
    }, {});
  } catch (_) {
    // noop – 조회 실패 시 빈값 처리 생략
  }
  setRows(prev => {
      const selectedIdx = prev.findIndex(row => row[keyField] === src[keyField]);
      // 복사행의 keyField 값 생성 - 서버 rowKey(양수)와 절대 겹치지 않도록 음수로 채번(nextTempKey 참고)
      const maxKey = nextTempKey();

      const newRow = {
          ...src,
          ...pkBlankFields,
          ...rowInit,
          [keyField]: maxKey,
          no: '',
          cud: 'C',
      };
      const nextRows = [
          ...prev.slice(0, selectedIdx + 1),
          newRow,
          ...prev.slice(selectedIdx + 1)
      ];
      setTimeout(() => {
          if (gridApi) {
              gridApi.deselectAll();
              gridApi.forEachNode((node: any, i: number) => {
                  if (i === selectedIdx + 1) {
                      node.setSelected(true);
                      gridApi.ensureIndexVisible(i, 'middle');
                  }
              });
          }
      }, 0);
      return nextRows;
  });
}

/////////////////////////////////////////////////////////////////////////////////////////////////////////
// function : setCancelRow 행취소
// 설명     : 그리드에서 선택된 행을 {생성/삭제/복사/취소} 처리하기
// 파라미터 :
//   - gridApi: any - 그리드 API 객체
//   - setRows: (fn: (prev: any[]) => any[]) => void - 행 상태 업데이트 함수
//   - onFocusRow?: (row: any) => void - 취소 후 포커스가 이동한 행을 알려줌(2026-09-11 추가).
//     포커스 이동이 setSelected()로 프로그램에 의해 이루어져 onRowClicked가 발생하지 않으므로,
//     하위 그리드 자동조회 등 행클릭에 연동된 화면별 로직을 여기서 다시 걸어줄 수 있게 콜백으로 노출.
// 작성자   : 박동근
// 최종수정일 : 2025-10-11
/////////////////////////////////////////////////////////////////////////////////////////////////////////
export function setCancelRow(gridApi: any, setRows: (fn: (prev: any[]) => any[]) => void, onFocusRow?: (row: any) => void) {
  // 2026-09-11: 조회전용(agGridType==='Q') 그리드는 행취소 무력화.
  if (isQueryOnlyGridApi(gridApi)) return;
  const selectedGrid = gridApi.getSelectedRows();
  if (!selectedGrid || selectedGrid.length === 0) return;
  // 2026-09-24: 멀티선택(2행 이상)이면 선택된 행 전체에 일괄 적용 - 신규행(C)은 제거, 그 외는 _origin(조회 당시 값)으로 복원
  const cancelKeys = new Set<any>(selectedGrid.map((r: any) => r.rowKey));
  const originStore = originStoreByApi.get(gridApi);
  const originByKey = new Map<any, any>(selectedGrid.map((r: any) => [r.rowKey, r._origin ?? originStore?.get(r.rowKey)]));
  setRows(prev => {
      const cancelRows = prev
          .filter(row =>
              // cud가 'C'인 경우(신규행)는 삭제
              !(cancelKeys.has(row.rowKey) && row.cud === 'C')
          )
          .map(row =>
              // 취소 대상행이고 신규행이 아니면 _origin값으로 복원
              cancelKeys.has(row.rowKey) && row.cud !== 'C'
                  ? { ...row, ...originByKey.get(row.rowKey), cud: '' }
                  : row
          );
      // 취소된 행 중 가장 위 행의 바로 이전 행으로 포커스 이동
      const cancelIdx = prev.findIndex(row => cancelKeys.has(row.rowKey));
      setTimeout(() => {
          if (gridApi) {
              gridApi.deselectAll();
              let focusIdx = cancelIdx;
              // cud가 'C'인 경우(신규행)는 이전 행으로 포커스 이동
              if (prev[cancelIdx]?.cud === 'C') {
                  focusIdx = cancelIdx > 0 ? cancelIdx - 1 : 0;
              }
              gridApi.forEachNode((node: any, i: number) => {
                  if (i === focusIdx) {
                      node.setSelected(true);
                      gridApi.ensureIndexVisible(i, 'middle');
                  }
              });
              onFocusRow?.(cancelRows[focusIdx]);
          }
      }, 0);
      return cancelRows;
  });
}

/////////////////////////////////////////////////////////////////////////////////////////////////////////
// function : setCellValue 셀값설정
// 설명     : 그리드에서 선택된 행의 특정 필드값을 설정하기
// 파라미터 :
//   - gridApi: any - 그리드 API 객체
//   - setRows: (fn: (prev: any[]) => any[]) => void - 행 상태 업데이트 함수
//   - values: Record<string, any> - 설정할 필드명과 값의 객체
////////////////////////////////////////////////////////////////////////////////////////////////////////////
export function setCellValue(gridApi: any, setRows: (fn: (prev: any[]) => any[]) => void, values: Record<string, any>) {
    // 선택된 행 가져오기
    const selectedGrid = gridApi?.getSelectedRows?.();
    if (!selectedGrid || selectedGrid.length === 0) return;
    const selectedRowKey = selectedGrid[0].rowKey;

    setRows(prev =>
        prev.map(row =>
            row.rowKey === selectedRowKey
                ? {
                    ...row,
                    ...values,
                    cud: (!row.cud || row.cud === '') ? 'U' : row.cud // cud가 ''이면 'U'로 변경
                }
                : row
        )
    );
}
/////////////////////////////////////////////////////////////////////////////////////////////////////////
// function : getActiveCellValue 셀값가져오기
// 설명     : 선택된 행(없으면 첫 표시 행)의 특정 필드 값을 반환
// 파라미터 :
//   - agGrid: any - AgGridReact ref 또는 gridApi 유사 객체
//   - field: string - 가져올 필드명
// 반환값   : any | undefined - 필드 값(없으면 undefined)
// 사용예시 : const userId = getActiveCellValue(agGrid1, 'user_id');
///////////////////////////////////////////////////////////////////////////////////////////////////////////
export function getActiveCellValue(agGrid: any, field: string): any | undefined {
  // gridApi 해석: ref.current.api 또는 api 속성 혹은 자체가 api로 사용 가능한 객체
  const gridApi: any = agGrid?.current?.api ?? agGrid?.api ?? agGrid;
  if (!gridApi) return undefined;

  try {
    // 1) 선택된 행에서 우선 조회
    const selected = typeof gridApi.getSelectedRows === 'function' ? gridApi.getSelectedRows() : [];
    const row = selected && selected.length > 0
      ? selected[0]
      : (typeof gridApi.getDisplayedRowAtIndex === 'function'
          ? gridApi.getDisplayedRowAtIndex(0)?.data
          : undefined);
    if (!row) return undefined;
    return (row as any)?.[field];
  } catch (_) {
    return undefined;
  }
}

/////////////////////////////////////////////////////////////////////////////////////////////////////////
// function : getRowCellValue 셀값가져오기
// 설명     : 행의 특정 필드 값을 반환
// 파라미터 :
//   - agGrid: any - AgGridReact ref 또는 gridApi 유사 객체
//   - field: string - 가져올 필드명
// 반환값   : any | undefined - 필드 값(없으면 undefined)
// 사용예시 : const userId = getRowCellValue(agGrid1, 1, 'user_id');
///////////////////////////////////////////////////////////////////////////////////////////////////////////
export function getRowCellValue(agGrid: any, rowIndex: number, field: string): any | undefined {
  const gridApi: any = agGrid?.current?.api ?? agGrid?.api ?? agGrid;
  if (!gridApi) return undefined;
  try {
    const getCount = typeof gridApi.getDisplayedRowCount === 'function' ? gridApi.getDisplayedRowCount() : undefined;
    const getRowAt = typeof gridApi.getDisplayedRowAtIndex === 'function' ? gridApi.getDisplayedRowAtIndex : undefined;
    if (!getCount || !getRowAt) return undefined;

    const count = getCount.call(gridApi);
    if (!Number.isFinite(count) || count <= 0) return undefined;

    // clamp index into [0, count-1]
    let idx = Number(rowIndex);
    if (!Number.isFinite(idx)) return undefined;
    if (idx < 0) idx = 0;
    if (idx >= count) idx = count - 1;

    const node = getRowAt.call(gridApi, idx);
    const data = node?.data;
    return data ? (data as any)[field] : undefined;
  } catch (_) {
    return undefined;
  }
}

export default AgGrid;
